/**
 * 契約管理 - Jspreadsheet（Excelライク・範囲コピー対応）画面
 *
 * Jspreadsheet CE を使用してExcelのような操作性を実現。
 * セル範囲選択、コピー&ペースト、直接編集が可能。
 */
import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, Chip, Alert, CircularProgress, Menu, MenuItem as MuiMenuItem,
  ListItemIcon, ListItemText, Divider, TextField, InputAdornment, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel, FormGroup,
  GlobalStyles,
} from '@mui/material';
import {
  Save as SaveIcon,
  Add as AddIcon,
  Download as DownloadIcon,
  UploadFile as UploadFileIcon,
  VisibilityOff as VisibilityOffIcon,
  Visibility as VisibilityIcon,
  ViewColumn as ViewColumnIcon,
  Edit as EditIcon,
  AddCircle as AddCircleIcon,
  Delete as DeleteIcon2,
  WarningAmber as WarningIcon,
  ContentCopy as CopyIcon,
  ContentPaste as PasteIcon,
  ArrowBack as ArrowLeftIcon,
  ArrowForward as ArrowRightIcon,
  AddCircleOutline as InsertRowIcon,
  RemoveCircleOutline as DeleteRowIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Comment as CommentIcon,
  Palette as PaletteIcon,
  FormatColorReset as ClearColorIcon,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useNavigationGuard } from '../contexts/NavigationGuardContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import jspreadsheet from 'jspreadsheet-ce';
import 'jspreadsheet-ce/dist/jspreadsheet.css';
import 'jsuites/dist/jsuites.css'; // required for the calendar (date) cell editor popup styling
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useSnackbar } from '../components/AppSnackbar';
import {
  type CellMetaMap,
  CELL_COLOR_PALETTE,
  parseCellMeta,
  serializeCellMeta,
  cellNameFromCoords,
} from '../utils/contractCellMeta';

// ============================================================
// Types
// ============================================================

interface ContractItem {
  id: string;
  customerId: string;
  contractType: string | null;
  systemName: string;
  totalFee: number | null;
  monthlyFee: number | null;
  fiveYearFee: number | null;
  paymentMonth: string | null;
  startDate: string | null;
  renewalDate: string | null;
  personInCharge: string | null;
  svCount: number | null;
  clCount: number | null;
  mbCount: number | null;
  svOs: string | null;
  clOs: string | null;
  mbOs: string | null;
  systemVer: string | null;
  dbVer: string | null;
  notes: string | null;
  cellMeta: string | null;
  managerId: string | null;
  notificationDaysOverride: number | null;
  customer: { id: string; name: string; customerCode: string };
  manager?: { id: string; name: string } | null;
}

// ============================================================
// Column config
// ============================================================

const COLUMNS = [
  { title: '顧客', width: 150 },
  { title: 'システム名', width: 180 },
  { title: '合計保守料金', width: 120 },
  { title: '月額', width: 100 },
  { title: '5年計', width: 100 },
  { title: '支払月', width: 80 },
  { title: '契約形態', width: 100 },
  { title: '担当者', width: 100 },
  { title: 'SV台数', width: 70 },
  { title: 'CL台数', width: 70 },
  { title: 'MB台数', width: 70 },
  { title: 'SV_OS', width: 120 },
  { title: 'CL_OS', width: 120 },
  { title: 'MB_OS', width: 120 },
  { title: 'システム_Ver', width: 120 },
  { title: 'DB_Ver', width: 100 },
  { title: '契約開始日', width: 110 },
  { title: '更新日', width: 110 },
  { title: '備考', width: 200 },
  { title: '管理者', width: 120 },
  { title: '通知日数', width: 80 },
];

// 必須入力の列（COLUMNS 上の元インデックス）: 顧客(0) のみ。
// システム名は任意（contract.service.ts の validate 参照）。
// 未入力（空文字列または空白のみ）のセルだけをオレンジ枠で強調する。
const REQUIRED_ORIG_COLUMNS: number[] = [0];
// 未入力強調に使う枠線色（プロジェクト共通の警告色）。
const REQUIRED_HIGHLIGHT_COLOR = '#ed6c02';

// Parse a cell value into a number for saving. Handles values that may come
// back from a masked numeric column as a comma-grouped string (e.g. "1,000").
// Returns null for empty/blank values so the field is stored as NULL.
function toNumberOrNull(value: string | number | null): number | null {
  if (value === null || value === undefined) return null;
  const raw = String(value).replace(/,/g, '').trim();
  if (raw === '') return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
}

function contractToRow(c: ContractItem): (string | number | null)[] {
  return [
    c.customer?.name || '',
    c.systemName || '',
    c.totalFee,
    c.monthlyFee,
    c.fiveYearFee,
    c.paymentMonth || '',
    c.contractType || '',
    c.personInCharge || '',
    c.svCount,
    c.clCount,
    c.mbCount,
    c.svOs || '',
    c.clOs || '',
    c.mbOs || '',
    c.systemVer || '',
    c.dbVer || '',
    c.startDate ? c.startDate.substring(0, 10) : '',
    c.renewalDate ? c.renewalDate.substring(0, 10) : '',
    c.notes || '',
    c.manager?.name || '',
    c.notificationDaysOverride,
  ];
}

// ============================================================
// Column state persistence helpers
// ============================================================

function getColumnStateKey(userId: string): string {
  return `contract-excel-col-state-${userId}`;
}

interface ColumnSavedState {
  widths: number[];
  order: number[];
  /** Original column indices that are currently hidden (per-user persisted). */
  hidden?: number[];
}

function loadColumnState(userId: string): ColumnSavedState | null {
  try {
    const stored = localStorage.getItem(getColumnStateKey(userId));
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

/**
 * Resolve a dropdown cell's RAW value into its display label.
 *
 * jspreadsheet-ce stores a dropdown cell's value as the INDEX into the
 * column's `source` array once the user picks an option from the dropdown,
 * but the initial data we load writes the label string directly. So getData()
 * returns a label for untouched rows and a numeric index for rows the user
 * changed. Looking that index up in customerMap fails, which made "change the
 * customer then save" report a save failure. Normalise both shapes here.
 */
function resolveDropdownLabel(raw: string | number | null, source: string[]): string {
  const value = String(raw ?? '').trim();
  if (value === '') return '';
  if (/^\d+$/.test(value)) {
    const byIndex = source[Number(value)];
    if (byIndex !== undefined) return byIndex;
  }
  return value;
}

// ============================================================
// Component
// ============================================================

const ContractSpreadsheetPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { showSuccess, showError } = useSnackbar();
  const queryClient = useQueryClient();
  const jRef = useRef<HTMLDivElement>(null);
  const spreadsheetRef = useRef<any>(null);
  const isAdmin = user?.role === 'admin';
  const userId = user?.id || 'anonymous';

  const urlCustomerId = searchParams.get('customerId') || '';
  const urlCustomerName = searchParams.get('customerName') || '';

  const [contractIds, setContractIds] = useState<string[]>([]);
  const contractIdsRef = useRef<string[]>([]);
  // IDs marked for deletion by the user (row removed from the grid) but not
  // yet sent to the backend. The actual DELETE requests are only issued when
  // "一括保存" (Save All) is clicked, so users can undo a deletion before saving.
  const pendingDeleteIdsRef = useRef<Set<string>>(new Set());
  // IDs of existing rows whose CONTENT (cell values) changed since the last
  // save, and — separately — rows whose COLOR/COMMENT (cellMeta) changed.
  // These are tracked independently so that on "一括保存" content updates and
  // color/comment updates are sent as SEPARATE requests:
  //   - content changes  → PUT /contracts/:id      (does NOT touch cellMeta)
  //   - color/comment     → PATCH /contracts/:id/cell-meta (only cellMeta)
  // This means a user who only changed colors won't overwrite another user's
  // content edits on the same row, and vice versa (each concerns is last-write
  // -wins at the row level, but the two concerns don't clobber each other).
  // Brand-new rows (id starting with "__new_") are always saved via POST.
  // Tracked by ID (not row index) so it stays correct across sorting, row
  // insert/delete and drag-reordering.
  const dirtyContentRowIdsRef = useRef<Set<string>>(new Set());
  const dirtyMetaRowIdsRef = useRef<Set<string>>(new Set());

  // Dirty counts for the unsaved-changes indicator (title *, status bar, nav guard).
  const [dirtyCounts, setDirtyCounts] = useState<{ modified: number; added: number; deleted: number }>({ modified: 0, added: 0, deleted: 0 });
  const isDirty = dirtyCounts.modified > 0 || dirtyCounts.added > 0 || dirtyCounts.deleted > 0;
  // Stack of id-batches removed via row deletion, in chronological order of
  // deletion. Used to restore the correct ids when a deletion is undone, and
  // to re-mark them for deletion when that undo is redone.
  const deletedBatchStackRef = useRef<string[][]>([]);
  const [customerMap, setCustomerMap] = useState<Map<string, { id: string; name: string }>>(new Map());
  const [searchText, setSearchText] = useState('');
  // Controls whether the ▼ filter icon is shown/clickable on column headers
  // (toggled by the "フィルター" button). Existing column filters remain
  // active while hidden — this only affects the icon's visibility and the
  // ability to open the filter dialog, not previously-applied filters.
  const [showFilters, setShowFilters] = useState(false);

  // Excel-like column filters (replaces jspreadsheet-ce's built-in filter row).
  // Keyed by ORIGINAL column index (COLUMNS array order, not display order),
  // so filters stay attached to the correct column even after drag-reordering.
  // A missing entry means "no filter active" for that column. Kept in a ref
  // (not React state) since visibility is applied imperatively via
  // showRow/hideRow and doesn't need to trigger re-renders on its own.
  const columnFilterSelectionsRef = useRef<Map<number, Set<string>>>(new Map());
  /** Color filter: origCol → set of selected colors (hex). Special value '__none__' = no color. */
  const colorFilterSelectionsRef = useRef<Map<number, Set<string>>>(new Map());

  // ============================================================
  // Column visibility (Excel-like hide / unhide columns)
  // ============================================================
  // Set of ORIGINAL column indices that are currently hidden. Kept in state so
  // the "表示列" dialog re-renders, and mirrored into a ref for synchronous
  // access inside jspreadsheet callbacks. Persisted per user in localStorage.
  const [hiddenColumns, setHiddenColumns] = useState<Set<number>>(() => {
    const saved = loadColumnState(user?.id || 'anonymous');
    return new Set(saved?.hidden ?? []);
  });
  const hiddenColumnsRef = useRef<Set<number>>(hiddenColumns);
  hiddenColumnsRef.current = hiddenColumns;
  // "表示列" (column visibility) dialog open state
  const [columnVisibilityDialogOpen, setColumnVisibilityDialogOpen] = useState(false);
  const [filterDialog, setFilterDialog] = useState<{
    origCol: number;
    title: string;
    /** All unique display values currently present in this column, sorted. */
    allValues: string[];
    /** Working selection for this dialog session (not yet applied until OK). */
    selected: Set<string>;
    search: string;
    allColors: string[];
    selectedColors: Set<string>;
  } | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number; mouseY: number; col: number; row: number;
    /** True if the right-click happened while a multi-cell range was highlighted (not just a single cell). */
    hasRangeSelection: boolean;
    /**
     * Full selection range at the moment of right-click: [colStart, rowStart, colEnd, rowEnd].
     * MUI's Menu renders in a React Portal outside the spreadsheet's DOM container, so
     * jspreadsheet-ce's own "click outside resets selection" behavior clears
     * `highlighted`/`selectedCell` as soon as the menu opens. We snapshot the range here
     * and re-apply it via `restoreSelection()` right before performing any action, so
     * copy/paste/color-fill still operate on the originally selected range.
     */
    range: [number, number, number, number];
    /** True when the right-click happened on a column header (thead) cell. */
    isHeader?: boolean;
  } | null>(null);

  // Status bar info (Excel-like info at the bottom showing selection stats)
  const [statusBar, setStatusBar] = useState<{
    count: number;
    numericCount: number;
    sum: number;
    avg: number;
    min: number;
    max: number;
  } | null>(null);

  // Pending rAF id for a deferred status-bar computation, so rapid selection
  // changes (during a drag) coalesce into a single update per frame.
  const statusBarRafRef = useRef<number | null>(null);

  // Defer status-bar computation to the next animation frame. This keeps the
  // work OFF jspreadsheet's synchronous selection path (see onselection) so
  // range selection / drag behaviour is never disturbed. Reads values via the
  // ref (no stale closures) and only touches React state inside the rAF.
  const scheduleStatusBarUpdate = useCallback((_inst: any, x1: number, y1: number, x2: number, y2: number) => {
    if (statusBarRafRef.current !== null) {
      cancelAnimationFrame(statusBarRafRef.current);
    }
    statusBarRafRef.current = requestAnimationFrame(() => {
      statusBarRafRef.current = null;
      try {
        // NOTE: jspreadsheet-ce's onselection passes the DOM element (not the
        // instance) as the first arg, so we read values from the instance ref
        // instead. Bail out if it's not ready.
        const inst = spreadsheetRef.current as any;
        if (!inst) return;
        const colStart = Math.min(x1, x2);
        const colEnd = Math.max(x1, x2);
        const rowStart = Math.min(y1, y2);
        const rowEnd = Math.max(y1, y2);
        const count = (colEnd - colStart + 1) * (rowEnd - rowStart + 1);
        let numericCount = 0;
        let sum = 0;
        let min = Infinity;
        let max = -Infinity;
        for (let r = rowStart; r <= rowEnd; r++) {
          for (let c = colStart; c <= colEnd; c++) {
            const raw = inst.getValueFromCoords(c, r);
            const str = String(raw ?? '').replace(/,/g, '').trim();
            if (str === '') continue;
            const n = Number(str);
            if (!Number.isNaN(n)) {
              numericCount++;
              sum += n;
              if (n < min) min = n;
              if (n > max) max = n;
            }
          }
        }
        if (numericCount === 0) {
          setStatusBar({ count, numericCount: 0, sum: 0, avg: 0, min: 0, max: 0 });
        } else {
          setStatusBar({ count, numericCount, sum, avg: sum / numericCount, min, max });
        }
      } catch {
        /* ignore */
      }
    });
  }, []);

  // Re-select the range that was highlighted when the context menu was
  // opened. Must be called before any context-menu action that depends on
  // the current selection (copy, paste, color fill), since opening the MUI
  // menu (rendered in a portal) causes jspreadsheet-ce to reset selection.
  const restoreContextSelection = useCallback(() => {
    if (!contextMenu) return;
    const [x1, y1, x2, y2] = contextMenu.range;
    spreadsheetRef.current?.updateSelectionFromCoords(x1, y1, x2, y2);
  }, [contextMenu]);
  const columnOrderRef = useRef<number[]>([]);
  // The 顧客 dropdown's `source` list as it was handed to jspreadsheet. Needed
  // when saving because a dropdown cell's raw value is the INDEX into this
  // array once the user picks an option (see resolveDropdownLabel).
  const dropdownSourceRef = useRef<string[]>([]);
  // Guards against infinite recursion when onmovecolumn programmatically
  // snaps the frozen columns (顧客/システム名) back to the front.
  const columnMoveSnapBackRef = useRef(false);
  // 直前に「右クリック→コピー」でクリップボードへ書き込んだテキスト。貼り付け時に
  // 一致すれば同一グリッド内コピーと判断し、jspreadsheet の内部コピーデータを
  // paste に渡して色も復元する（範囲コピーでも色が付く）。
  const lastCopiedTextRef = useRef<string | null>(null);

  // Comment dialog state (右クリック→コメント編集)
  const [commentDialog, setCommentDialog] = useState<{ col: number; row: number; value: string } | null>(null);

  // Keep contractIdsRef in sync with contractIds state, and provide a helper
  // to update both at once (ref is needed for synchronous access inside
  // jspreadsheet event callbacks, e.g. ondeleterow, where React state updates
  // would not be visible yet).
  const updateContractIds = useCallback((updater: (prev: string[]) => string[]) => {
    setContractIds((prev) => {
      const next = updater(prev);
      contractIdsRef.current = next;
      return next;
    });
  }, []);

  // Mark the row at the given display index as having CONTENT changes (cell
  // value edits/pastes). New rows (id "__new_") are always saved via POST, so
  // tracking them here is unnecessary.
  const markRowContentDirtyByIndex = useCallback((rowIndex: number) => {
    const id = contractIdsRef.current[rowIndex];
    if (id && !id.startsWith('__new_')) {
      dirtyContentRowIdsRef.current.add(id);
    }
  }, []);

  // Mark the row at the given display index as having COLOR/COMMENT (cellMeta)
  // changes. Sent separately from content via PATCH /cell-meta on save.
  const markRowMetaDirtyByIndex = useCallback((rowIndex: number) => {
    const id = contractIdsRef.current[rowIndex];
    if (id && !id.startsWith('__new_')) {
      dirtyMetaRowIdsRef.current.add(id);
    }
  }, []);

  // Recalculate dirty counts from the current refs. Call this after every
  // action that changes dirty state (edit, color, add row, delete row, undo,
  // redo, save). This drives the title "*", status bar counts, and nav guard.
  const updateDirtyCounts = useCallback(() => {
    const modified = dirtyContentRowIdsRef.current.size + dirtyMetaRowIdsRef.current.size;
    const added = contractIdsRef.current.filter((id) => id.startsWith('__new_')).length;
    const deleted = pendingDeleteIdsRef.current.size;
    setDirtyCounts({ modified, added, deleted });
  }, []);

  // Handle undo of a row deletion: restore the ids that were removed (popped
  // from deletedBatchStackRef, matching the LIFO order jspreadsheet's own
  // history uses) and clear their pending-delete flag.
  const restoreDeletedBatch = useCallback((entry: any) => {
    const position = entry.insertBefore ? entry.rowNumber : entry.rowNumber + 1;
    const count = entry.numOfRows || 1;
    const restoredIds = deletedBatchStackRef.current.pop()
      ?? Array.from({ length: count }, (_, i) => `__new_${Date.now()}_${i}`);
    restoredIds.forEach((id) => pendingDeleteIdsRef.current.delete(id));
    const next = [
      ...contractIdsRef.current.slice(0, position),
      ...restoredIds,
      ...contractIdsRef.current.slice(position),
    ];
    contractIdsRef.current = next;
    setContractIds(next);
  }, []);

  // Handle redo of a row deletion (or undo of a row insertion): remove the
  // ids at `position` from contractIds and re-mark them for deletion (pushing
  // back onto the stack so a subsequent undo can restore them again).
  const removeRowBatch = useCallback((entry: any, isDeleteAction: boolean) => {
    const position = entry.insertBefore ? entry.rowNumber : entry.rowNumber + 1;
    const count = entry.numOfRows || 1;
    const removedIds = contractIdsRef.current.slice(position, position + count);
    if (isDeleteAction) {
      // Redoing a deletion: mark these ids as pending delete again.
      removedIds.filter((id) => id && !id.startsWith('__new_')).forEach((id) => pendingDeleteIdsRef.current.add(id));
      deletedBatchStackRef.current.push(removedIds);
    } else {
      // Undoing an insertion: these were never-saved placeholder rows, no
      // pending-delete bookkeeping needed.
      removedIds.forEach((id) => pendingDeleteIdsRef.current.delete(id));
    }
    const next = [
      ...contractIdsRef.current.slice(0, position),
      ...contractIdsRef.current.slice(position + count),
    ];
    contractIdsRef.current = next;
    setContractIds(next);
  }, []);

  // Fetch contracts
  const { data, isLoading, error } = useQuery<{ data: ContractItem[] }>({
    queryKey: ['contracts-spreadsheet', urlCustomerId],
    queryFn: async () => {
      const params = urlCustomerId ? `?customerId=${urlCustomerId}` : '';
      const res = await api.get(`/contracts${params}`);
      return res.data;
    },
  });

  // Fetch customers for name→id mapping and dropdown
  const { data: customersData } = useQuery<{ data: { id: string; customerCode: string; name: string }[] }>({
    queryKey: ['customers-list'],
    queryFn: async () => {
      const res = await api.get('/customers');
      return res.data;
    },
  });

  const customerNames = useMemo(
    () => (customersData?.data ?? []).map((c) => c.name),
    [customersData],
  );

  // Build customer name→id map
  useEffect(() => {
    if (customersData?.data) {
      const map = new Map<string, { id: string; name: string }>();
      customersData.data.forEach((c) => map.set(c.name, { id: c.id, name: c.name }));
      setCustomerMap(map);
    }
  }, [customersData]);

  // Fetch users for the manager dropdown (admin-only)
  const { data: usersData } = useQuery<{ data: { id: string; name: string; email: string }[] } | { id: string; name: string; email: string }[]>({
    queryKey: ['users-list'],
    queryFn: async () => {
      const res = await api.get('/users');
      return res.data;
    },
    enabled: isAdmin,
  });

  const managerNames = useMemo(() => {
    const raw = usersData as any;
    const list: { id: string; name: string }[] = Array.isArray(raw) ? raw : (raw?.data ?? []);
    return list.map((u) => u.name);
  }, [usersData]);

  const [managerMap, setManagerMap] = useState<Map<string, { id: string; name: string }>>(new Map());

  useEffect(() => {
    const raw = usersData as any;
    const list: { id: string; name: string }[] = Array.isArray(raw) ? raw : (raw?.data ?? []);
    if (list.length > 0) {
      const map = new Map<string, { id: string; name: string }>();
      list.forEach((u) => map.set(u.name, { id: u.id, name: u.name }));
      setManagerMap(map);
    }
  }, [usersData]);

  // Measure the real rendered widths of the row-number column and the first
  // display column (both frozen via CSS sticky), and expose them as CSS
  // custom properties consumed by the sx block below. This keeps the second
  // frozen data column positioned flush against the first, regardless of
  // how wide either column actually is (avoids the overlap/gap that occurs
  // when jspreadsheet-ce's own freezeColumns hardcodes a fixed offset).
  const updateFreezeOffsets = useCallback(() => {
    const container = jRef.current;
    if (!container) return;
    const firstDataRow = container.querySelector('tbody tr');
    if (!firstDataRow) return;
    const rowNumCell = firstDataRow.querySelector('td.jexcel_row') as HTMLElement | null;
    const col0Cell = firstDataRow.querySelector('td[data-x="0"]') as HTMLElement | null;
    const col0Width = rowNumCell?.offsetWidth ?? 0;
    const col1Left = col0Width + (col0Cell?.offsetWidth ?? 0);
    container.style.setProperty('--freeze-offset-col0', `${col0Width}px`);
    container.style.setProperty('--freeze-offset-col1', `${col1Left}px`);
  }, []);

  // Save column state to localStorage
  const saveColumnStateToStorage = useCallback(() => {
    if (!spreadsheetRef.current) return;
    const widths: number[] = [];
    for (let i = 0; i < COLUMNS.length; i++) {
      widths.push(spreadsheetRef.current.getWidth(i) || COLUMNS[i].width);
    }
    const order = columnOrderRef.current.length === COLUMNS.length ? [...columnOrderRef.current] : COLUMNS.map((_, i) => i);
    const hidden = Array.from(hiddenColumnsRef.current);
    localStorage.setItem(getColumnStateKey(userId), JSON.stringify({ widths, order, hidden }));
  }, [userId]);

  // ============================================================
  // Column visibility helpers
  // ============================================================
  // Apply the current hidden-column set to the live spreadsheet using
  // jspreadsheet's showColumn/hideColumn (which only toggle display CSS, so
  // data/history are untouched). Also marks the header cell immediately BEFORE
  // each hidden run with a class so we can draw an Excel-like thin boundary.
  const applyColumnVisibility = useCallback(() => {
    const inst = spreadsheetRef.current;
    if (!inst) return;
    const currentOrder = columnOrderRef.current;
    const hidden = hiddenColumnsRef.current;

    for (let displayIdx = 0; displayIdx < currentOrder.length; displayIdx++) {
      const origIdx = currentOrder[displayIdx];
      if (hidden.has(origIdx)) {
        inst.hideColumn(displayIdx);
      } else {
        inst.showColumn(displayIdx);
      }
    }

    // Mark visible headers that sit immediately after a hidden run, so CSS can
    // render a thicker left border there (Excel's hidden-column indicator).
    for (let displayIdx = 0; displayIdx < currentOrder.length; displayIdx++) {
      const headerCell = inst.headers?.[displayIdx] as HTMLElement | undefined;
      if (!headerCell) continue;
      const prevOrig = displayIdx > 0 ? currentOrder[displayIdx - 1] : undefined;
      const prevIsHidden = prevOrig !== undefined && hidden.has(prevOrig);
      const selfHidden = hidden.has(currentOrder[displayIdx]);
      headerCell.classList.toggle('hidden-col-boundary', prevIsHidden && !selfHidden);
    }
  }, []);

  // Hide the given ORIGINAL column indices (persisted + applied).
  const hideColumnsByOrigIdx = useCallback((origIdxs: number[]) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      origIdxs.forEach((i) => next.add(i));
      hiddenColumnsRef.current = next;
      return next;
    });
  }, []);

  // Show the given ORIGINAL column indices.
  const showColumnsByOrigIdx = useCallback((origIdxs: number[]) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      origIdxs.forEach((i) => next.delete(i));
      hiddenColumnsRef.current = next;
      return next;
    });
  }, []);

  // Toggle a single column's visibility (used by the 表示列 dialog).
  const toggleColumnVisibility = useCallback((origIdx: number) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(origIdx)) next.delete(origIdx);
      else next.add(origIdx);
      hiddenColumnsRef.current = next;
      return next;
    });
  }, []);

  // Show every hidden column.
  const showAllColumns = useCallback(() => {
    setHiddenColumns(() => {
      const next = new Set<number>();
      hiddenColumnsRef.current = next;
      return next;
    });
  }, []);

  // Whenever the hidden-column set changes, re-apply it to the grid and
  // persist to localStorage so it survives reloads / next login.
  useEffect(() => {
    applyColumnVisibility();
    saveColumnStateToStorage();
  }, [hiddenColumns, applyColumnVisibility, saveColumnStateToStorage]);

  // For the right-clicked display column, find the contiguous run(s) of hidden
  // columns adjacent to it (before and/or after) so "列を再表示" can unhide the
  // whole run at once — matching Excel's behaviour of selecting the columns
  // surrounding a hidden gap.
  const getAdjacentHiddenOrigIdxs = useCallback((displayIdx: number): number[] => {
    const currentOrder = columnOrderRef.current;
    const hidden = hiddenColumnsRef.current;
    const result: number[] = [];
    // Walk left
    for (let i = displayIdx - 1; i >= 0; i--) {
      const orig = currentOrder[i];
      if (hidden.has(orig)) result.push(orig);
      else break;
    }
    // Walk right
    for (let i = displayIdx + 1; i < currentOrder.length; i++) {
      const orig = currentOrder[i];
      if (hidden.has(orig)) result.push(orig);
      else break;
    }
    return result;
  }, []);

  // ============================================================
  // Excel-like column filter dialog (replaces jspreadsheet-ce's built-in
  // filter row entirely).
  //
  // Design notes:
  // - Filter selections are stored keyed by ORIGINAL column index (not
  //   display index) in columnFilterSelectionsRef, so they remain correctly
  //   attached to a column even if the user drags columns to reorder them.
  // - "Row matches" = for every column with an active filter, that column's
  //   cell text is in the filter's selected-values set. Filters across
  //   different columns combine with AND (a row must pass every active
  //   filter), matching the requirement "複数列のフィルタを同時に適用可能".
  // - Visibility is applied via jspreadsheet-ce's own showRow()/hideRow(),
  //   which only toggles the row's `display` CSS — it does NOT remove rows
  //   from the underlying data/history, so this composes safely with
  //   Undo/Redo, row insert/delete, and handleSaveAll (which reads
  //   getData()/contractIds by index, unaffected by hidden rows).
  // ============================================================

  const applyColumnFilters = useCallback(() => {
    const inst = spreadsheetRef.current;
    if (!inst) return;
    const currentOrder = columnOrderRef.current;
    const activeFilters = Array.from(columnFilterSelectionsRef.current.entries());
    const activeColorFilters = Array.from(colorFilterSelectionsRef.current.entries());
    const rowCount = contractIdsRef.current.length;
    const styleMap = (inst as any).options?.style ?? (inst as any).style;

    // Track rows that transition from hidden to visible
    if (activeFilters.length === 0 && activeColorFilters.length === 0) {
      // No active filters: show all hidden rows
      for (let row = 0; row < rowCount; row++) {
        const tr = (inst as any).rows?.[row] as HTMLElement | undefined;
        if (tr && tr.style.display === 'none') {
          inst.showRow(row);
        }
      }
    } else {
      for (let row = 0; row < rowCount; row++) {
        let matches = true;
        for (const [origCol, selectedValues] of activeFilters) {
          const displayCol = currentOrder.indexOf(origCol);
          if (displayCol === -1) continue;
          const cellValue = String(inst.getValueFromCoords(displayCol, row) ?? '').trim();
          if (!selectedValues.has(cellValue)) {
            matches = false;
            break;
          }
        }
        if (matches) {
          for (const [origCol, selectedColors] of activeColorFilters) {
            const displayCol = currentOrder.indexOf(origCol);
            if (displayCol === -1) continue;
            const cellName = cellNameFromCoords(displayCol, row);
            const styleStr = styleMap?.[cellName] as string | undefined;
            const colorMatch = styleStr ? /background-color\s*:\s*([^;]+)/i.exec(styleStr) : null;
            const cellColor = colorMatch ? colorMatch[1].trim().toLowerCase() : '__none__';
            if (!selectedColors.has(cellColor)) {
              matches = false;
              break;
            }
          }
        }
        if (matches) {
          const tr = (inst as any).rows?.[row] as HTMLElement | undefined;
          if (tr && tr.style.display === 'none') {
            inst.showRow(row);
          }
        } else {
          inst.hideRow(row);
        }
      }
    }

    // Re-apply cell background colors from jspreadsheet's internal style
    // record for ALL VISIBLE rows after filter changes.
    if (styleMap) {
      for (let row = 0; row < rowCount; row++) {
        const tr = (inst as any).rows?.[row] as HTMLElement | undefined;
        if (tr && tr.style.display === 'none') continue; // skip hidden rows
        for (let displayIdx = 0; displayIdx < currentOrder.length; displayIdx++) {
          const cellName = cellNameFromCoords(displayIdx, row);
          const styleStr = styleMap[cellName] as string | undefined;
          if (!styleStr) continue;
          const colorMatch = /background-color\s*:\s*([^;]+)/i.exec(styleStr);
          if (colorMatch) {
            const cell = inst.getCellFromCoords(displayIdx, row) as HTMLElement | null;
            if (cell) {
              cell.style.backgroundColor = colorMatch[1].trim();
            }
          }
        }
      }
    }
  }, []);

  const isColumnFiltered = useCallback(
    (origCol: number) => columnFilterSelectionsRef.current.has(origCol) || colorFilterSelectionsRef.current.has(origCol),
    [],
  );

  // Toggle a CSS class on each header cell to visually indicate which
  // columns currently have an active filter (Excel shows a filled/blue
  // funnel icon for filtered columns — we swap the ▼ icon's color via this
  // class instead of swapping icons).
  const updateFilterIconIndicators = useCallback(() => {
    const inst = spreadsheetRef.current;
    if (!inst?.headers) return;
    columnOrderRef.current.forEach((origCol: number, displayCol: number) => {
      const headerCell = inst.headers[displayCol] as HTMLElement | undefined;
      if (!headerCell) return;
      headerCell.classList.toggle('column-filter-active', isColumnFiltered(origCol));
    });
  }, [isColumnFiltered]);

  // Open the filter dialog for the column at display index `displayCol`,
  // pre-populated with that column's current unique values and the
  // previously-selected values (if any) for this column.
  const openFilterDialog = useCallback((displayCol: number) => {
    const inst = spreadsheetRef.current;
    if (!inst) return;
    const origCol = columnOrderRef.current[displayCol];
    if (origCol === undefined) return;

    const rowCount = contractIdsRef.current.length;
    const uniqueValues = new Set<string>();
    const uniqueColors = new Set<string>();
    let hasNoColor = false;
    const styleMap = (inst as any).options?.style ?? (inst as any).style;
    for (let row = 0; row < rowCount; row++) {
      uniqueValues.add(String(inst.getValueFromCoords(displayCol, row) ?? '').trim());
      const cellName = cellNameFromCoords(displayCol, row);
      const styleStr = styleMap?.[cellName] as string | undefined;
      const colorMatch = styleStr ? /background-color\s*:\s*([^;]+)/i.exec(styleStr) : null;
      if (colorMatch) {
        uniqueColors.add(colorMatch[1].trim().toLowerCase());
      } else {
        hasNoColor = true;
      }
    }
    const allValues = Array.from(uniqueValues).sort((a, b) => a.localeCompare(b, 'ja'));

    const existingSelection = columnFilterSelectionsRef.current.get(origCol);
    // Default (no filter yet applied): everything selected.
    const selected = existingSelection ? new Set(existingSelection) : new Set(allValues);

    const allColors: string[] = [];
    if (hasNoColor) allColors.push('__none__');
    uniqueColors.forEach((c) => allColors.push(c));

    const existingColorSelection = colorFilterSelectionsRef.current.get(origCol);
    const selectedColors = existingColorSelection ? new Set(existingColorSelection) : new Set(allColors);

    setFilterDialog({
      origCol,
      title: COLUMNS[origCol]?.title ?? '',
      allValues,
      selected,
      search: '',
      allColors,
      selectedColors,
    });
  }, []);

  const handleFilterDialogToggleValue = (value: string) => {
    setFilterDialog((prev) => {
      if (!prev) return prev;
      const next = new Set(prev.selected);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return { ...prev, selected: next };
    });
  };

  // "全選択"/"全解除" only affect the values currently visible under the
  // search box's narrowing (要件: 検索で絞り込まれている場合は絞り込まれた
  // 対象のみが対象). When the search box is empty, "currently visible" is
  // simply all values, so behavior is unchanged in that case. Selections for
  // values hidden by the search text are left untouched either way.
  const getVisibleFilterValues = (dialog: { allValues: string[]; search: string }): string[] => {
    const term = dialog.search.trim().toLowerCase();
    if (!term) return dialog.allValues;
    return dialog.allValues.filter((v) => v.toLowerCase().includes(term));
  };

  const handleFilterDialogSelectAll = () => {
    setFilterDialog((prev) => {
      if (!prev) return prev;
      // Select exactly the currently-visible (search-narrowed) values;
      // anything hidden by the search text is explicitly deselected rather
      // than left in its previous state (要件: それ以外の項目は未選択状態とする).
      const visible = getVisibleFilterValues(prev);
      return { ...prev, selected: new Set(visible) };
    });
  };

  const handleFilterDialogDeselectAll = () => {
    setFilterDialog((prev) => {
      if (!prev) return prev;
      const visible = getVisibleFilterValues(prev);
      const next = new Set(prev.selected);
      visible.forEach((v) => next.delete(v));
      return { ...prev, selected: next };
    });
  };

  // As soon as the search box narrows the candidate list, automatically
  // select exactly the values currently matching the search text and
  // deselect everything else (要件: 検索で絞り込まれた段階で表示中の項目
  // のみを選択状態にし、検索に一致しない項目は明示的に未選択にする).
  // Clearing the search box (back to empty) restores "everything selected"
  // as the default, matching the dialog's initial-open behavior.
  const handleFilterDialogSearchChange = (newSearch: string) => {
    setFilterDialog((prev) => {
      if (!prev) return prev;
      const term = newSearch.trim().toLowerCase();
      const visible = term ? prev.allValues.filter((v) => v.toLowerCase().includes(term)) : prev.allValues;
      return { ...prev, search: newSearch, selected: new Set(visible) };
    });
  };

  const handleFilterDialogOk = () => {
    if (!filterDialog) return;
    const { origCol, selected, allValues, selectedColors, allColors } = filterDialog;
    // If every value is selected, treat it as "no filter" for this column
    // (removes it from the active-filters map rather than storing a no-op set).
    if (selected.size === allValues.length) {
      columnFilterSelectionsRef.current.delete(origCol);
    } else {
      columnFilterSelectionsRef.current.set(origCol, new Set(selected));
    }
    if (selectedColors.size === allColors.length) {
      colorFilterSelectionsRef.current.delete(origCol);
    } else {
      colorFilterSelectionsRef.current.set(origCol, new Set(selectedColors));
    }
    setFilterDialog(null);
    applyColumnFilters();
    updateFilterIconIndicators();

    // After the dialog closes (next tick), MUI unmounts the Dialog portal and
    // jspreadsheet detects the focus/click change, resetting its selection
    // (.highlight classes removed). That reset can strip the inline
    // background-color from cells. Re-apply cell colors AFTER this reset
    // completes by deferring to the next animation frame.
    requestAnimationFrame(() => {
      const inst = spreadsheetRef.current as any;
      if (!inst) return;
      const currentOrder = columnOrderRef.current;
      const rowCount = contractIdsRef.current.length;
      const styleMap = inst.options?.style ?? inst.style;
      if (!styleMap) return;
      for (let row = 0; row < rowCount; row++) {
        const tr = inst.rows?.[row] as HTMLElement | undefined;
        if (tr && tr.style.display === 'none') continue;
        for (let displayIdx = 0; displayIdx < currentOrder.length; displayIdx++) {
          const cellName = cellNameFromCoords(displayIdx, row);
          const styleStr = styleMap[cellName] as string | undefined;
          if (!styleStr) continue;
          const colorMatch = /background-color\s*:\s*([^;]+)/i.exec(styleStr);
          if (colorMatch) {
            const cell = inst.getCellFromCoords(displayIdx, row) as HTMLElement | null;
            if (cell) {
              cell.style.backgroundColor = colorMatch[1].trim();
            }
          }
        }
      }
    });
  };

  const handleFilterDialogCancel = () => {
    // Discard any in-progress checkbox changes; previously-applied filters
    // (columnFilterSelectionsRef) are untouched.
    setFilterDialog(null);
  };

  // Filtered-value selections are cleared whenever the underlying data set
  // changes (e.g. after "一括保存" refetches contracts), since the set of
  // valid values for a column may have changed and stale selections could
  // silently hide rows the user doesn't expect. This mirrors how the old
  useEffect(() => {
    columnFilterSelectionsRef.current.clear();
    colorFilterSelectionsRef.current.clear();
  }, [data]);

  // ============================================================
  // 必須項目の未入力強調
  // ============================================================
  // Required columns (顧客 = orig 0) that are empty (blank or whitespace-only)
  // get an orange OUTLINE. Applies to both existing and newly-added rows, and
  // clears itself as soon as a value is entered.
  //
  // `outline` is used deliberately instead of box-shadow: the selection frame
  // (.highlight-* classes) already owns box-shadow as a single property, and
  // background-color is reserved for the user's cell-color feature. outline is
  // an independent property that doesn't affect layout, so it coexists with
  // the selection frame and any cell text coloring.
  const applyRequiredFieldHighlight = useCallback(() => {
    const inst = spreadsheetRef.current;
    if (!inst) return;
    const currentOrder = columnOrderRef.current;
    const requiredDisplayIdxs = new Set(
      REQUIRED_ORIG_COLUMNS
        .map((origIdx) => currentOrder.indexOf(origIdx))
        .filter((displayIdx) => displayIdx !== -1),
    );
    const rowCount = (inst.getData() as unknown[][]).length;
    // Sweep EVERY column, not just the required ones. Reordering columns moves
    // the cell elements between display positions, so an outline applied at the
    // required column's OLD display index stays on whatever column now occupies
    // it — leaving a stray orange frame on a non-required column. Clearing all
    // columns first makes this function idempotent regardless of column order.
    for (let rowIdx = 0; rowIdx < rowCount; rowIdx++) {
      for (let displayIdx = 0; displayIdx < currentOrder.length; displayIdx++) {
        const cell = inst.getCellFromCoords(displayIdx, rowIdx) as HTMLElement | null;
        if (!cell) continue;
        const blank =
          requiredDisplayIdxs.has(displayIdx) &&
          String(inst.getValueFromCoords(displayIdx, rowIdx) ?? '').trim() === '';
        cell.style.outline = blank ? `2px solid ${REQUIRED_HIGHLIGHT_COLOR}` : '';
        cell.style.outlineOffset = blank ? '-2px' : '';
      }
    }
  }, []);

  // Initialize/update spreadsheet
  useEffect(() => {
    if (!jRef.current || !data?.data) return;

    // Destroy existing instance
    if (spreadsheetRef.current) {
      try { jspreadsheet.destroy(jRef.current as any); } catch { /* ignore */ }
      spreadsheetRef.current = null;
    }

    const contracts = data.data;
    const ids = contracts.map((c) => c.id);
    setContractIds(ids);
    contractIdsRef.current = ids;

    const rowData = contracts.map(contractToRow);

    // The 顧客 column is a dropdown, and jspreadsheet only DISPLAYS a value that
    // exists in the dropdown's `source` list. The customer list is fetched by a
    // separate query, so when the contract data arrives first the source would be
    // empty and every 顧客 cell would render blank. Merge the customer names that
    // actually appear in the loaded rows into the source so the cells always show
    // their value, regardless of which query resolves first.
    const namesInData = contracts
      .map((c) => c.customer?.name)
      .filter((n): n is string => !!n);
    const dropdownSource = Array.from(new Set([...customerNames, ...namesInData]));
    dropdownSourceRef.current = dropdownSource;

    // Load saved column state (widths + order)
    const savedState = loadColumnState(userId);
    let savedOrder = savedState?.order && savedState.order.length === COLUMNS.length ? savedState.order : COLUMNS.map((_, i) => i);
    // The 顧客 (orig index 0) and システム名 (orig index 1) columns are the two
    // horizontally-frozen columns and must always render as the first two
    // display columns, in that order — regardless of any previously-saved
    // column order (which could have been reordered before this constraint
    // existed). Pin them to the front and keep the rest in their saved order.
    savedOrder = [0, 1, ...savedOrder.filter((idx) => idx !== 0 && idx !== 1)];
    columnOrderRef.current = [...savedOrder];

    // Reorder columns and data according to saved order
    const orderedColumns = savedOrder.map((origIdx) => COLUMNS[origIdx]);
    const orderedRowData = rowData.map((row) => savedOrder.map((origIdx) => row[origIdx]));

    // Build the `style` init option (cell background colors) from each
    // contract's persisted cellMeta, translating original column indices to
    // the current display order (savedOrder).
    const styleInit: Record<string, string> = {};
    contracts.forEach((contract, rowIdx) => {
      const meta = parseCellMeta(contract.cellMeta);
      Object.entries(meta).forEach(([origColStr, entry]) => {
        const origCol = Number(origColStr);
        const displayCol = savedOrder.indexOf(origCol);
        if (displayCol === -1 || !entry.color) return;
        styleInit[cellNameFromCoords(displayCol, rowIdx)] = `background-color:${entry.color}`;
      });
    });

    const columnsConfig = orderedColumns.map((col, idx) => {
      const origIdx = savedOrder[idx];
      const width = savedState?.widths?.[idx] ?? col.width;
      const base: Record<string, unknown> = {
        title: col.title,
        width,
        readOnly: !isAdmin,
      };
      // Money columns (合計保守料金=2, 月額=3, 5年計=4): show thousands
      // separators and right-align the values. `mask: '#,##0'` renders the
      // stored number with comma grouping (no decimals); jspreadsheet keeps
      // the raw numeric value internally so save-time Number() parsing is
      // unaffected. Header stays centered (only the cell body is right-aligned).
      if (origIdx === 2 || origIdx === 3 || origIdx === 4) {
        base.type = 'numeric';
        base.mask = '#,##0';
        base.align = 'right';
      }
      // Customer column uses dropdown with autocomplete
      if (origIdx === 0) {
        base.type = 'dropdown';
        base.source = dropdownSource;
        base.autocomplete = true;
        base.readOnly = !isAdmin;
      }
      // Date columns (契約開始日, 更新日) use a calendar picker (日本語表示)
      // 明細上の表示形式は YYYY/MM/DD（例: 2026/07/01）
      if (origIdx === 16 || origIdx === 17) {
        base.type = 'calendar';
        base.options = {
          format: 'YYYY/MM/DD',
          months: [
            '1月', '2月', '3月', '4月', '5月', '6月',
            '7月', '8月', '9月', '10月', '11月', '12月',
          ],
          weekdays: ['日', '月', '火', '水', '木', '金', '土'],
          weekdays_short: ['日', '月', '火', '水', '木', '金', '土'],
        };
      }
      // Customer (顧客, orig 0), system name (システム名, orig 1) and notes
      // (備考, orig 18) are left-aligned (jspreadsheet's default is centered).
      if (origIdx === 0 || origIdx === 1 || origIdx === 18) {
        base.align = 'left';
      }
      // Manager column (管理者, orig 19): dropdown of user names (admin-only)
      if (origIdx === 19) {
        base.type = 'dropdown';
        base.source = managerNames;
        base.autocomplete = true;
        base.align = 'left';
      }
      // Notification days (通知日数, orig 20): numeric, right-aligned
      if (origIdx === 20) {
        base.type = 'numeric';
        base.mask = '#,##0';
        base.align = 'right';
      }
      return base;
    });

    spreadsheetRef.current = jspreadsheet(jRef.current as any, {
      data: orderedRowData.length > 0 ? orderedRowData : [Array(COLUMNS.length).fill('')],
      columns: columnsConfig,
      defaultColWidth: 100,
      tableOverflow: true,
      // tableWidth is intentionally NOT set (no '100%') so the table
      // renders at its natural width (sum of all column widths). Combined
      // with tableOverflow:true this keeps horizontal scrolling INSIDE the
      // .jexcel_content element rather than overflowing the page, so the
      // page-level layout (sidebar, header buttons) stays fixed in view.
      tableHeight: '100%',
      columnSorting: true,
      columnDrag: true,
      filters: false, // custom Excel-like filter dialog is used instead (see openFilterDialog/applyColumnFilters)
      allowInsertRow: isAdmin,
      allowDeleteRow: isAdmin,
      allowInsertColumn: false,
      allowDeleteColumn: false,
      selectionCopy: true,
      copyCompatibility: true,
      search: false,
      pagination: 0,
      // NOTE: jspreadsheet-ce's own freezeColumns is intentionally NOT used.
      // Its tbody freeze positioning hardcodes a "-51px" offset that assumes
      // a default-width row-number column; combined with our custom sticky
      // row-number column below, that mismatch caused the frozen "顧客"
      // column to overlap/hide behind the row numbers. Instead, the leftmost
      // 3 columns (row number + first 2 data columns) are frozen entirely
      // via plain CSS `position: sticky`, with left offsets computed from
      // the real column widths in `updateFreezeOffsets` below.
      allowComments: true,
      style: styleInit,
      // Track value edits so only modified rows are saved. Fires for direct
      // cell edits AND for pastes (jspreadsheet-ce calls onchange per changed
      // cell), so paste-modified rows are covered here too.
      onchange: (_instance: any, _cell: any, _x: any, y: any) => {
        markRowContentDirtyByIndex(Number(y));
        updateUndoRedoButtons();
        // Re-evaluate the required-but-empty highlight for the edited row.
        applyRequiredFieldHighlight();
      },
      onresizecolumn: (_instance, _col, _width) => {
        saveColumnStateToStorage();
        updateFreezeOffsets();
      },
      // NOTE on the first callback argument: jspreadsheet-ce dispatches events
      // as `obj.dispatch('onmovecolumn', el, from, to)`, so the first argument
      // is the grid's CONTAINER DOM ELEMENT — NOT the spreadsheet instance.
      // Calling `instance.moveColumn(...)` on it throws a TypeError, which
      // aborted the rest of this handler (column-state persistence, freeze
      // offsets and the required-field outline) and left
      // columnMoveSnapBackRef stuck at `true`. Always go through
      // spreadsheetRef.current instead, and never let a throw skip the
      // bookkeeping below.
      onmovecolumn: (_el: any, from: number, to: number) => {
        // A re-entrant call triggered by our own snap-back moveColumn() below
        // is flagged so it only updates order tracking without re-snapping.
        const isSnapBack = columnMoveSnapBackRef.current;

        // Update column order tracking
        const order = columnOrderRef.current;
        const [moved] = order.splice(from, 1);
        order.splice(to, 0, moved);

        if (!isSnapBack) {
          // Enforce that 顧客 (orig 0) and システム名 (orig 1) always remain the
          // first two display columns. If a drag displaced them (or dragged
          // another column ahead of them), snap the affected column back.
          const inst = spreadsheetRef.current;
          const snapBack = (origIdx: number, fixedPos: number) => {
            const current = order.indexOf(origIdx);
            if (!inst || current === -1 || current === fixedPos) return;
            columnMoveSnapBackRef.current = true;
            try {
              inst.moveColumn(current, fixedPos);
            } finally {
              // Always clear the guard, even if moveColumn throws — otherwise
              // every later column move is mistaken for a snap-back and the
              // frozen columns stop being enforced entirely.
              columnMoveSnapBackRef.current = false;
            }
          };
          snapBack(0, 0);
          snapBack(1, 1);
        }

        saveColumnStateToStorage();
        updateFreezeOffsets();
        applyRequiredFieldHighlight();
      },
      onundo: (_instance: any, entry: any) => {
        // entry is the history action that was just undone.
        // Undoing an insertRow removes those rows; undoing a deleteRow restores them.
        if (entry?.action === 'insertRow') removeRowBatch(entry, false);
        else if (entry?.action === 'deleteRow') restoreDeletedBatch(entry);
        else if (entry?.action === 'orderBy' && Array.isArray(entry.rows)) {
          const order: number[] = entry.rows;
          const inverse: string[] = [];
          const prevIds = contractIdsRef.current;
          for (let l = 0; l < order.length; l++) inverse[order[l]] = prevIds[l];
          contractIdsRef.current = inverse;
          setContractIds(inverse);
        }
        // Restore cursor to the affected cell(s) after undo of a setValue
        // (single cell edit). jspreadsheet sometimes fails to do this on its
        // own, leaving the highlight in a wrong position.
        if (entry?.action === 'setValue' && Array.isArray(entry.records) && entry.records.length > 0) {
          const first = entry.records[0];
          const last = entry.records[entry.records.length - 1];
          const inst = spreadsheetRef.current;
          if (inst && first && last) {
            inst.updateSelectionFromCoords(first.x, first.y, last.x, last.y);
          }
        }
        updateUndoRedoButtons();
        // Undoing a deleteRow (or the insertRow-redo below) brings back rows
        // whose cells start fully visible — hideColumn() only ever touched
        // the rows that existed when it was called. Without re-applying the
        // hidden-column set, those rows show columns the rest of the grid
        // has hidden, so their cells sit shifted relative to every other row
        // (see handleAddRow for the full explanation).
        applyColumnVisibility();
        applyRequiredFieldHighlight();
      },
      onredo: (_instance: any, entry: any) => {
        // entry is the history action that was just redone.
        // Redoing an insertRow re-inserts rows; redoing a deleteRow re-removes them.
        if (entry?.action === 'orderBy' && Array.isArray(entry.rows)) {
          // Redo of a sort: re-apply the forward permutation to contractIds.
          const order: number[] = entry.rows;
          const prevIds = contractIdsRef.current;
          const reordered = order.map((origIdx) => prevIds[origIdx]);
          contractIdsRef.current = reordered;
          setContractIds(reordered);
        } else if (entry?.action === 'insertRow') {
          // Re-inserted rows are fresh placeholders again (no ids to restore).
          const position = entry.insertBefore ? entry.rowNumber : entry.rowNumber + 1;
          const count = entry.numOfRows || 1;
          const placeholders = Array.from({ length: count }, (_, i) => `__new_${Date.now()}_${i}`);
          const next = [
            ...contractIdsRef.current.slice(0, position),
            ...placeholders,
            ...contractIdsRef.current.slice(position),
          ];
          contractIdsRef.current = next;
          setContractIds(next);
        } else if (entry?.action === 'deleteRow') {
          removeRowBatch(entry, true);
        }
        // Restore cursor to the affected cell(s) after redo of a setValue
        if (entry?.action === 'setValue' && Array.isArray(entry.records) && entry.records.length > 0) {
          const first = entry.records[0];
          const last = entry.records[entry.records.length - 1];
          const inst = spreadsheetRef.current;
          if (inst && first && last) {
            inst.updateSelectionFromCoords(first.x, first.y, last.x, last.y);
          }
        }
        updateUndoRedoButtons();
        applyColumnVisibility();
        applyRequiredFieldHighlight();
      },
      ondeleterow: (_instance: any, rowNumber: number, numOfRows: number) => {
        // Do NOT call the DELETE API here. Just remove the rows from our
        // tracked id list and mark them for deletion; the actual DELETE
        // requests are sent when the user clicks "一括保存" (Save All), so a
        // deletion can still be undone (Ctrl+Z) before that happens.
        const removedIds = contractIdsRef.current.slice(rowNumber, rowNumber + numOfRows);
        deletedBatchStackRef.current.push(removedIds);
        removedIds.filter((id) => id && !id.startsWith('__new_')).forEach((id) => pendingDeleteIdsRef.current.add(id));

        const remainingIds = [
          ...contractIdsRef.current.slice(0, rowNumber),
          ...contractIdsRef.current.slice(rowNumber + numOfRows),
        ];
        contractIdsRef.current = remainingIds;
        setContractIds(remainingIds);
        updateUndoRedoButtons();
        applyRequiredFieldHighlight();
      },
      contextMenu: () => [] as object[], // Disable built-in, use custom MUI menu
      // Selection callback: update the status bar with stats about the
      // selected range. CRITICAL: jspreadsheet calls onselection SYNCHRONOUSLY
      // in the middle of its own drag-selection handling. Running React state
      // updates or heavy work here interferes with (and can abort) that
      // handling, breaking range selection. So we (1) wrap in try/catch so an
      // error can never bubble into jspreadsheet, and (2) DEFER the actual
      // work to a requestAnimationFrame so it runs AFTER jspreadsheet finishes
      // its selection update, fully decoupled from the critical path.
      // Column sort: jspreadsheet reorders its internal data/rows when a
      // header is sorted, but our contractIds array (which maps grid row index
      // -> contract id) is NOT automatically reordered. That mismatch caused
      // saves to write a row's values to the WRONG contract id. jspreadsheet
      // records the applied permutation in the just-pushed history entry
      // (action:"orderBy", rows:[...] = new order of original indices), so we
      // read it and apply the SAME permutation to contractIds to keep them
      // aligned with the visible rows.
      onsort: (_el: any, _column: string | number, _order: 0 | 1) => {
        try {
          const inst = spreadsheetRef.current as any;
          const history = inst?.history;
          const historyIndex = inst?.historyIndex;
          const entry = Array.isArray(history) && historyIndex >= 0 ? history[historyIndex] : null;
          if (entry && entry.action === 'orderBy' && Array.isArray(entry.rows)) {
            const order: number[] = entry.rows;
            const prevIds = contractIdsRef.current;
            const reordered = order.map((origIdx) => prevIds[origIdx]);
            contractIdsRef.current = reordered;
            setContractIds(reordered);
          }
        } catch {
          /* never let id-resync errors break sorting */
        }
        applyRequiredFieldHighlight();
      },
      // Selection callback: update the status bar with stats about the
      // selected range. CRITICAL: jspreadsheet calls onselection SYNCHRONOUSLY
      // in the middle of its own drag-selection handling. Running React state
      // updates or heavy work here interferes with (and can abort) that
      // handling, breaking range selection. So we (1) wrap in try/catch so an
      // error can never bubble into jspreadsheet, and (2) DEFER the actual
      // work to a requestAnimationFrame so it runs AFTER jspreadsheet finishes
      // its selection update, fully decoupled from the critical path.
      onselection: (inst: any, x1: number, y1: number, x2: number, y2: number) => {
        try {
          scheduleStatusBarUpdate(inst, x1, y1, x2, y2);
        } catch {
          /* never let status-bar errors break selection */
        }
      },
    });

    // Apply persisted comments (jspreadsheet-ce has no init option for
    // comments, so they must be set via setComments after construction).
    contracts.forEach((contract, rowIdx) => {
      const meta = parseCellMeta(contract.cellMeta);
      Object.entries(meta).forEach(([origColStr, entry]) => {
        const origCol = Number(origColStr);
        const displayCol = savedOrder.indexOf(origCol);
        if (displayCol === -1 || !entry.comment) return;
        spreadsheetRef.current?.setComments(cellNameFromCoords(displayCol, rowIdx), entry.comment, '');
      });
    });

    // Measure the real widths of the row-number column and the first display
    // column, then expose them as CSS vars so the sticky-positioned columns
    // (see sx block below) sit flush against each other with no gap/overlap.
    // Re-run whenever a column is resized or moved, since widths can change.
    updateFreezeOffsets();
    const contentEl = jRef.current?.querySelector('.jexcel_content') as HTMLElement | null;
    contentEl?.addEventListener('scroll', updateFreezeOffsets);
    window.addEventListener('resize', updateFreezeOffsets);

    // Suppress browser's "Save password?" dialog: jspreadsheet creates <input>
    // elements dynamically for cell editing and dropdown search. Browsers'
    // password managers interpret these as login forms. Setting autocomplete=off
    // on each input as it's added prevents the dialog.
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            if (node.tagName === 'INPUT') node.setAttribute('autocomplete', 'off');
            node.querySelectorAll?.('input')?.forEach((input) => input.setAttribute('autocomplete', 'off'));
          }
        });
      }
    });
    if (jRef.current) observer.observe(jRef.current, { childList: true, subtree: true });

    // Re-apply any previously-set column filters to this freshly-built
    // instance (e.g. after a data refetch from "一括保存"), and refresh the
    // ▼ icon indicators for filtered columns.
    applyColumnFilters();
    updateFilterIconIndicators();
    updateUndoRedoButtons();
    // Restore per-user hidden columns on the freshly built instance.
    applyColumnVisibility();
    // Highlight required-but-empty cells (顧客) with an orange outline.
    applyRequiredFieldHighlight();

    return () => {
      contentEl?.removeEventListener('scroll', updateFreezeOffsets);
      window.removeEventListener('resize', updateFreezeOffsets);
      observer.disconnect();
    };
    // NOTE: applyRequiredFieldHighlight is intentionally EXCLUDED from the deps
    // array. Including such apply-* helpers has previously caused a
    // re-render → re-run infinite loop that blanked the screen
    // (docs/excel-like-ui-spec.md section 20). The callback is stable
    // (useCallback with []) and reads the latest refs internally.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isAdmin, customerNames, userId, saveColumnStateToStorage, restoreDeletedBatch, removeRowBatch, updateFreezeOffsets, applyColumnFilters, updateFilterIconIndicators, markRowContentDirtyByIndex]);

  // ============================================================
  // Auto-scroll when dragging a selection beyond the grid edge
  // ============================================================
  // While the user holds the mouse button and drags past the visible area of
  // .jexcel_content, smoothly scroll in that direction and continuously
  // extend the selection. Speed increases the further the cursor is from the
  // edge (mimicking Excel's behaviour).
  useEffect(() => {
    const container = jRef.current?.querySelector('.jexcel_content') as HTMLElement | null;
    if (!container) return;

    const EDGE_ZONE = 40; // px from edge where scrolling starts
    const MIN_SPEED = 2; // px per frame at the edge
    const MAX_SPEED = 24; // px per frame far outside

    let isDragging = false;
    let animFrameId: number | null = null;
    let mouseX = 0;
    let mouseY = 0;

    function getSpeed(distance: number): number {
      // distance: how far outside the edge (0 = at edge, >0 = further out)
      const t = Math.min(distance / 80, 1); // normalise 0–1 over 80px
      return MIN_SPEED + (MAX_SPEED - MIN_SPEED) * t * t; // quadratic ease
    }

    function extendSelectionToPoint(clientX: number, clientY: number) {
      const inst = spreadsheetRef.current;
      if (!inst) return;
      // Find the cell under the cursor
      const el = document.elementFromPoint(clientX, clientY);
      if (!el) return;
      const td = (el as HTMLElement).closest('td[data-x]') as HTMLElement | null;
      if (!td) return;
      const x = Number(td.getAttribute('data-x'));
      const y = Number(td.getAttribute('data-y'));
      if (Number.isNaN(x) || Number.isNaN(y)) return;
      // Extend selection from the original anchor to this cell
      const sel = (inst as any).selectedCell;
      if (sel) {
        inst.updateSelectionFromCoords(sel[0], sel[1], x, y);
      }
    }

    function scrollLoop() {
      if (!isDragging) return;
      const rect = container!.getBoundingClientRect();
      let dx = 0;
      let dy = 0;

      if (mouseX < rect.left + EDGE_ZONE) {
        dx = -getSpeed(rect.left + EDGE_ZONE - mouseX);
      } else if (mouseX > rect.right - EDGE_ZONE) {
        dx = getSpeed(mouseX - (rect.right - EDGE_ZONE));
      }

      if (mouseY < rect.top + EDGE_ZONE) {
        dy = -getSpeed(rect.top + EDGE_ZONE - mouseY);
      } else if (mouseY > rect.bottom - EDGE_ZONE) {
        dy = getSpeed(mouseY - (rect.bottom - EDGE_ZONE));
      }

      if (dx !== 0 || dy !== 0) {
        container!.scrollLeft += dx;
        container!.scrollTop += dy;
        extendSelectionToPoint(mouseX, mouseY);
      }

      animFrameId = requestAnimationFrame(scrollLoop);
    }

    function onMouseDown(e: MouseEvent) {
      if (e.button !== 0) return;
      const target = e.target as HTMLElement;
      // Only engage when the press starts inside the data area (tbody cells).
      if (!target.closest('.jexcel tbody')) return;
      isDragging = true;
      mouseX = e.clientX;
      mouseY = e.clientY;
      // Kick off the loop immediately; it self-cancels when no scrolling is
      // needed (cursor inside the grid) but keeps running while dragging.
      if (!animFrameId) {
        animFrameId = requestAnimationFrame(scrollLoop);
      }
    }

    function onMouseMove(e: MouseEvent) {
      if (!isDragging) return;
      mouseX = e.clientX;
      mouseY = e.clientY;
      if (!animFrameId) {
        animFrameId = requestAnimationFrame(scrollLoop);
      }
    }

    function onMouseUp() {
      isDragging = false;
      if (animFrameId) {
        cancelAnimationFrame(animFrameId);
        animFrameId = null;
      }
    }

    // Use the CAPTURE phase on window so we always see the mousedown first,
    // even though jspreadsheet-ce attaches its own mousedown handler and may
    // stop propagation (which would prevent a bubble-phase listener on the
    // container from ever firing — the reason auto-scroll appeared dead).
    window.addEventListener('mousedown', onMouseDown, true);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
      window.removeEventListener('mousedown', onMouseDown, true);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      if (animFrameId) cancelAnimationFrame(animFrameId);
    };
  }, [data]); // Re-attach when grid rebuilds

  // ============================================================
  // Select-all + Ctrl+C with headers
  // ============================================================
  // Track whether the user last clicked the "select-all" cell (top-left corner
  // at the intersection of header row and row-number column). When they press
  // Ctrl+C in that state, we copy ALL data with column headers (Excel-like).
  const isAllSelectedRef = useRef(false);

  useEffect(() => {
    const container = jRef.current;
    if (!container) return;

    // Click on the selectall cell → select all cells programmatically
    const onSelectAllClick = (e: Event) => {
      const target = e.target as HTMLElement;
      if (!target.closest('td.jexcel_selectall')) return;
      const inst = spreadsheetRef.current;
      if (!inst) return;
      const data = inst.getData() as unknown[][];
      if (data.length === 0) return;
      const lastRow = data.length - 1;
      const lastCol = (data[0] as unknown[]).length - 1;
      inst.updateSelectionFromCoords(0, 0, lastCol, lastRow);
      isAllSelectedRef.current = true;
    };

    // Reset the all-selected flag whenever user clicks a normal cell
    const onCellClick = (e: Event) => {
      const target = e.target as HTMLElement;
      if (!target.closest('td.jexcel_selectall')) {
        isAllSelectedRef.current = false;
      }
    };

    // Intercept Ctrl+C: if all cells are selected, copy with headers
    const onKeyDown = async (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && isAllSelectedRef.current) {
        e.preventDefault();
        e.stopPropagation();
        const inst = spreadsheetRef.current;
        if (!inst) return;
        const currentOrder = columnOrderRef.current;
        const headers = currentOrder.map((origIdx) => COLUMNS[origIdx].title);
        const data = inst.getData() as (string | number | null)[][];
        const lines: string[] = [headers.join('\t')];
        for (let r = 0; r < data.length; r++) {
          lines.push(data[r].map((v) => String(v ?? '')).join('\t'));
        }
        try {
          await navigator.clipboard.writeText(lines.join('\n'));
        } catch { /* ignore */ }
      }
    };

    // Intercept the browser "paste" event (Ctrl+V) before jspreadsheet's own
    // handler. When the clipboard matches what we last copied within this grid
    // (右クリック→コピー or Ctrl+C), paste using jspreadsheet's internal copy
    // data (inst.data) so cell colors (styles) are restored. paste() only
    // restores styles when the pasted text's hash matches the copy-time hash,
    // which is only guaranteed with the internal data. clipboardData.getData
    // is synchronous so preventDefault() reliably stops the default paste.
    const onPaste = (e: ClipboardEvent) => {
      const inst = spreadsheetRef.current;
      if (!inst || lastCopiedTextRef.current === null) return;
      if (typeof inst.data !== 'string') return;
      const sel = (inst as any).selectedCell;
      if (!sel) return;
      const text = e.clipboardData?.getData('text') ?? '';
      if (text !== lastCopiedTextRef.current) return; // external clipboard
      e.preventDefault();
      e.stopPropagation();
      const x = Math.min(Number(sel[0]), Number(sel[2]));
      const y = Math.min(Number(sel[1]), Number(sel[3]));
      inst.paste(x, y, inst.data);
    };

    container.addEventListener('click', onSelectAllClick);
    container.addEventListener('mousedown', onCellClick);
    container.addEventListener('keydown', onKeyDown, true);
    container.addEventListener('paste', onPaste, true);

    return () => {
      container.removeEventListener('click', onSelectAllClick);
      container.removeEventListener('mousedown', onCellClick);
      container.removeEventListener('keydown', onKeyDown, true);
      container.removeEventListener('paste', onPaste, true);
    };
  }, [data]);

  // ============================================================
  // Column AutoFit — Double-click header border to auto-resize
  // ============================================================
  useEffect(() => {
    const container = jRef.current;
    if (!container) return;

    const MIN_WIDTH = 60;
    const MAX_WIDTH = 600;
    const PADDING = 16; // left + right cell padding

    // Reusable canvas context for measuring text width
    let canvasCtx: CanvasRenderingContext2D | null = null;
    function measureText(text: string, font: string): number {
      if (!canvasCtx) {
        const canvas = document.createElement('canvas');
        canvasCtx = canvas.getContext('2d');
      }
      if (!canvasCtx) return text.length * 8; // fallback
      canvasCtx.font = font;
      return canvasCtx.measureText(text).width;
    }

    function autoFitColumn(displayCol: number) {
      const inst = spreadsheetRef.current as any;
      if (!inst) return;

      // Measure header text
      const headerCell = inst.headers?.[displayCol] as HTMLElement | undefined;
      const headerFont = headerCell
        ? `${getComputedStyle(headerCell).fontWeight} ${getComputedStyle(headerCell).fontSize} ${getComputedStyle(headerCell).fontFamily}`
        : 'bold 13px sans-serif';
      const headerText = headerCell?.textContent || COLUMNS[columnOrderRef.current[displayCol]]?.title || '';
      let maxWidth = measureText(headerText, headerFont) + PADDING + 20; // +20 for filter icon space

      // Measure data cells — only visible rows (filtered) for performance
      const data = inst.getData() as (string | number | null)[][];
      const rows = inst.rows as HTMLElement[];
      const firstVisibleCell = inst.records?.[0]?.[displayCol] as HTMLElement | undefined;
      const cellFont = firstVisibleCell
        ? `${getComputedStyle(firstVisibleCell).fontSize} ${getComputedStyle(firstVisibleCell).fontFamily}`
        : '13px sans-serif';

      for (let r = 0; r < data.length; r++) {
        // Skip hidden rows (filter or hideRow)
        if (rows[r] && rows[r].style.display === 'none') continue;
        const val = String(data[r]?.[displayCol] ?? '');
        if (!val) continue;
        const w = measureText(val, cellFont) + PADDING;
        if (w > maxWidth) maxWidth = w;
      }

      // Clamp
      const finalWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Math.ceil(maxWidth)));

      // Apply
      inst.setWidth(displayCol, finalWidth);
      saveColumnStateToStorage();
      updateFreezeOffsets();
    }

    function autoFitAllColumns() {
      const currentOrder = columnOrderRef.current;
      const hidden = hiddenColumnsRef.current;
      for (let d = 0; d < currentOrder.length; d++) {
        if (hidden.has(currentOrder[d])) continue;
        autoFitColumn(d);
      }
    }

    // Detect dblclick on the right edge of a header cell (the resize handle
    // area, typically the rightmost 6px). jspreadsheet's resize handle sits
    // in this area; a double-click there means "auto-fit" (matching Excel).
    function onDblClick(e: MouseEvent) {
      const target = e.target as HTMLElement;
      const headerCell = target.closest('thead td[data-x]') as HTMLElement | null;
      if (!headerCell) return;

      // Check if the click was near the right border of the header cell
      const rect = headerCell.getBoundingClientRect();
      const rightEdge = rect.right;
      const clickX = e.clientX;
      if (rightEdge - clickX > 8) return; // not on the resize border

      const displayCol = Number(headerCell.getAttribute('data-x'));
      if (Number.isNaN(displayCol)) return;

      e.preventDefault();
      e.stopPropagation();

      if (e.shiftKey) {
        autoFitAllColumns();
      } else {
        autoFitColumn(displayCol);
      }
    }

    container.addEventListener('dblclick', onDblClick, true);

    return () => {
      container.removeEventListener('dblclick', onDblClick, true);
    };
  }, [data, saveColumnStateToStorage, updateFreezeOffsets]);

  // Add row
  const handleAddRow = () => {
    if (!spreadsheetRef.current) return;
    const emptyRow = Array(COLUMNS.length).fill('');
    emptyRow[0] = urlCustomerName; // Pre-fill customer
    // insertRow(data, rowIndex, insertBefore): when `data` is an array, its
    // elements are used directly as `data[b] = n[b]` for each column b — it
    // must be the ROW's values themselves, NOT wrapped in another array.
    // The previous call passed `[emptyRow]` (double-wrapped), which made
    // jspreadsheet write the ENTIRE emptyRow array into display column 0's
    // cell value (n[0] = emptyRow) while every other column got undefined→
    // ''. That corrupted the frozen 顧客 column with a stray array reference
    // instead of a string, producing a misaligned outline/dropdown after
    // adding a row. insertBefore=true inserts BEFORE rowIndex 0, so the new
    // row becomes index 0 — matching our contractIds splice at index 0 below.
    spreadsheetRef.current.insertRow(emptyRow, 0, true);
    updateContractIds((prev) => [`__new_${Date.now()}`, ...prev]);
    // A freshly-created row's cells start fully visible — hideColumn() only
    // touches the rows that existed when it was called. Re-apply the hidden-
    // column set so the new row's cells line up with every other row.
    applyColumnVisibility();
    applyRequiredFieldHighlight();
  };

  // Save all
  const handleSaveAll = async () => {
    if (!spreadsheetRef.current) return;

    const gridData = spreadsheetRef.current.getData() as (string | number | null)[][];
    const currentOrder = columnOrderRef.current;
    let successCount = 0;
    let errorCount = 0;

    // First, send DELETE requests for rows removed since the last save.
    const idsToDelete = Array.from(pendingDeleteIdsRef.current);
    let deleteSuccessCount = 0;
    let deleteErrorCount = 0;
    for (const id of idsToDelete) {
      try {
        await api.delete(`/contracts/${id}`);
        deleteSuccessCount++;
        pendingDeleteIdsRef.current.delete(id);
      } catch {
        deleteErrorCount++;
      }
    }

    // Build reverse map: for each display position, which original column index it represents
    // currentOrder[displayIdx] = originalIdx
    // We need: originalRow[originalIdx] = displayRow[displayIdx]
    for (let i = 0; i < gridData.length; i++) {
      const displayRow = gridData[i];

      // Map display row back to original COLUMNS order
      const originalRow: (string | number | null)[] = Array(COLUMNS.length).fill(null);
      for (let displayIdx = 0; displayIdx < currentOrder.length; displayIdx++) {
        const origIdx = currentOrder[displayIdx];
        originalRow[origIdx] = displayRow[displayIdx];
      }

      const customerName = resolveDropdownLabel(originalRow[0], dropdownSourceRef.current);
      const systemName = String(originalRow[1] || '').trim();

      // Skip only fully-empty rows (no customer AND no system name). A row
      // with a customer but no system name is still savable — system name is
      // no longer required. (When there's a customerId from the URL context,
      // treat the row as non-empty as long as any of the two is present.)
      if (!customerName && !systemName) continue;

      // Determine which concerns changed for this row. New rows are always
      // fully saved (POST). Existing rows are saved only for the concern(s)
      // that changed: content → PUT, color/comment → PATCH /cell-meta. These
      // are independent so one user's color change won't clobber another's
      // content edit (and vice versa).
      const rowId = contractIds[i];
      const isNewRow = !rowId || rowId.startsWith('__new_');

      // For brand-new rows: skip if the user hasn't entered anything beyond
      // the auto-filled customer name (i.e. all other cells are blank). This
      // prevents accidentally POSTing a duplicate / empty record when the user
      // adds a row but never fills it in before clicking "一括保存".
      if (isNewRow) {
        const hasContent = originalRow.some((val, idx) => {
          if (idx === 0) return false; // ignore customer name (pre-filled)
          const s = String(val ?? '').trim();
          return s !== '';
        });
        if (!hasContent) continue;
      }

      const contentDirty = isNewRow || dirtyContentRowIdsRef.current.has(rowId);
      const metaDirty = isNewRow || dirtyMetaRowIdsRef.current.has(rowId);
      if (!contentDirty && !metaDirty) continue;

      // Resolve customer name to ID
      const customer = customerMap.get(customerName);
      const customerId = customer?.id || urlCustomerId;

      if (!customerId) {
        errorCount++;
        continue;
      }

      // Collect this row's current cell background colors and comments
      // (keyed by original column index) and serialize them into cellMeta.
      // Only needed when meta changed (or for a brand-new row).
      const buildCellMeta = (): string | null => {
        const rowCellMeta: CellMetaMap = {};
        for (let displayIdx = 0; displayIdx < currentOrder.length; displayIdx++) {
          const origIdx = currentOrder[displayIdx];
          const cellName = cellNameFromCoords(displayIdx, i);
          const styleStr = spreadsheetRef.current!.getStyle(cellName) as string;
          const colorMatch = /background-color\s*:\s*([^;]+)/i.exec(styleStr || '');
          const comment = spreadsheetRef.current!.getComments(cellName) as string;
          if (colorMatch) {
            rowCellMeta[origIdx] = { ...(rowCellMeta[origIdx] || {}), color: colorMatch[1].trim() };
          }
          if (comment) {
            rowCellMeta[origIdx] = { ...(rowCellMeta[origIdx] || {}), comment };
          }
        }
        return serializeCellMeta(rowCellMeta);
      };

      try {
        if (isNewRow) {
          // New row: create with content AND cellMeta in a single POST.
          const managerNameResolved = resolveDropdownLabel(originalRow[19], managerNames);
          const managerIdResolved = managerNameResolved ? managerMap.get(managerNameResolved)?.id || null : null;
          const payload: Record<string, unknown> = {
            customerId,
            systemName,
            totalFee: toNumberOrNull(originalRow[2]),
            monthlyFee: toNumberOrNull(originalRow[3]),
            fiveYearFee: toNumberOrNull(originalRow[4]),
            paymentMonth: originalRow[5] ? String(originalRow[5]).trim() : null,
            contractType: originalRow[6] ? String(originalRow[6]).trim() : null,
            personInCharge: originalRow[7] ? String(originalRow[7]).trim() : null,
            svCount: originalRow[8] ? Number(originalRow[8]) : null,
            clCount: originalRow[9] ? Number(originalRow[9]) : null,
            mbCount: originalRow[10] ? Number(originalRow[10]) : null,
            svOs: originalRow[11] ? String(originalRow[11]).trim() : null,
            clOs: originalRow[12] ? String(originalRow[12]).trim() : null,
            mbOs: originalRow[13] ? String(originalRow[13]).trim() : null,
            systemVer: originalRow[14] ? String(originalRow[14]).trim() : null,
            dbVer: originalRow[15] ? String(originalRow[15]).trim() : null,
            startDate: originalRow[16] ? String(originalRow[16]).trim() : null,
            renewalDate: originalRow[17] ? String(originalRow[17]).trim() : null,
            notes: originalRow[18] ? String(originalRow[18]).trim() : null,
            managerId: managerIdResolved,
            notificationDaysOverride: toNumberOrNull(originalRow[20]),
            cellMeta: buildCellMeta(),
          };
          await api.post('/contracts', payload);
          successCount++;
        } else {
          // Existing row: send only the changed concern(s), each independently.
          // CONTENT → PUT (deliberately WITHOUT cellMeta so it won't overwrite
          // another user's color/comment changes).
          if (contentDirty) {
            const managerNameResolved2 = resolveDropdownLabel(originalRow[19], managerNames);
            const managerIdResolved2 = managerNameResolved2 ? managerMap.get(managerNameResolved2)?.id || null : null;
            const contentPayload: Record<string, unknown> = {
              customerId,
              systemName,
              totalFee: toNumberOrNull(originalRow[2]),
              monthlyFee: toNumberOrNull(originalRow[3]),
              fiveYearFee: toNumberOrNull(originalRow[4]),
              paymentMonth: originalRow[5] ? String(originalRow[5]).trim() : null,
              contractType: originalRow[6] ? String(originalRow[6]).trim() : null,
              personInCharge: originalRow[7] ? String(originalRow[7]).trim() : null,
              svCount: originalRow[8] ? Number(originalRow[8]) : null,
              clCount: originalRow[9] ? Number(originalRow[9]) : null,
              mbCount: originalRow[10] ? Number(originalRow[10]) : null,
              svOs: originalRow[11] ? String(originalRow[11]).trim() : null,
              clOs: originalRow[12] ? String(originalRow[12]).trim() : null,
              mbOs: originalRow[13] ? String(originalRow[13]).trim() : null,
              systemVer: originalRow[14] ? String(originalRow[14]).trim() : null,
              dbVer: originalRow[15] ? String(originalRow[15]).trim() : null,
              startDate: originalRow[16] ? String(originalRow[16]).trim() : null,
              renewalDate: originalRow[17] ? String(originalRow[17]).trim() : null,
              notes: originalRow[18] ? String(originalRow[18]).trim() : null,
              managerId: managerIdResolved2,
              notificationDaysOverride: toNumberOrNull(originalRow[20]),
            };
            await api.put(`/contracts/${rowId}`, contentPayload);
            dirtyContentRowIdsRef.current.delete(rowId);
          }
          // COLOR/COMMENT → PATCH cell-meta (only cellMeta, doesn't touch content).
          if (metaDirty) {
            await api.patch(`/contracts/${rowId}/cell-meta`, { cellMeta: buildCellMeta() });
            dirtyMetaRowIdsRef.current.delete(rowId);
          }
          successCount++;
        }
      } catch {
        errorCount++;
      }
    }

    queryClient.invalidateQueries({ queryKey: ['contracts-spreadsheet'] });
    queryClient.invalidateQueries({ queryKey: ['contracts'] });
    queryClient.invalidateQueries({ queryKey: ['customers'] });

    // Clear all dirty tracking after successful save
    dirtyContentRowIdsRef.current.clear();
    dirtyMetaRowIdsRef.current.clear();
    pendingDeleteIdsRef.current.clear();
    updateDirtyCounts();

    const totalErrorCount = errorCount + deleteErrorCount;
    const messages: string[] = [];
    if (successCount > 0 || errorCount > 0) messages.push(`保存: 成功${successCount}件${errorCount > 0 ? `、失敗${errorCount}件` : ''}`);
    if (deleteSuccessCount > 0 || deleteErrorCount > 0) messages.push(`削除: 成功${deleteSuccessCount}件${deleteErrorCount > 0 ? `、失敗${deleteErrorCount}件` : ''}`);

    if (messages.length === 0) {
      showSuccess('変更はありませんでした');
    } else if (totalErrorCount === 0) {
      showSuccess(messages.join(' / '));
    } else {
      showError(messages.join(' / '));
    }
  };

  // ============================================================
  // Context menu actions: copy / paste / cell color / comments
  // ============================================================

  // Copy the currently highlighted range to the clipboard (same action as Ctrl+C).
  //
  // NOTE: jspreadsheet-ce's own copy() writes to the clipboard internally via
  // the legacy document.execCommand('copy'), which requires the copy to
  // happen synchronously within a "trusted" user-gesture event. Because MUI's
  // Menu renders in a React Portal, the click handler here runs one event
  // tick removed from the original browser context menu gesture, and
  // execCommand('copy') silently fails (the clipboard keeps whatever Ctrl+C
  // last put there). We work around this by asking copy() to just return the
  // tab-delimited string (returnData=true, so it skips execCommand), then
  // write that string to the clipboard ourselves via the modern async
  // Clipboard API, which does not have that same restriction.
  const handleContextCopy = async () => {
    restoreContextSelection();
    const inst = spreadsheetRef.current;
    if (!inst || !contextMenu) {
      setContextMenu(null);
      return;
    }
    // jspreadsheetの内部コピー処理を先に呼び、スタイル情報（セル色）を内部
    // バッファに記録させる。同一グリッド内への貼り付け時に色が復元される。
    inst.copy(true);
    // Normal copy only covers VISIBLE columns (hidden ones are skipped, per
    // spec). We build the TSV ourselves from the selection range rather than
    // using jspreadsheet's copy(), which has no notion of hidden columns.
    const currentOrder = columnOrderRef.current;
    const hidden = hiddenColumnsRef.current;
    const [x1, y1, x2, y2] = contextMenu.range;
    const colStart = Math.min(x1, x2);
    const colEnd = Math.max(x1, x2);
    const rowStart = Math.min(y1, y2);
    const rowEnd = Math.max(y1, y2);
    const lines: string[] = [];
    for (let r = rowStart; r <= rowEnd; r++) {
      const cells: string[] = [];
      for (let c = colStart; c <= colEnd; c++) {
        const orig = currentOrder[c];
        if (orig !== undefined && hidden.has(orig)) continue; // skip hidden
        cells.push(String(inst.getValueFromCoords(c, r) ?? ''));
      }
      lines.push(cells.join('\t'));
    }
    const text = lines.join('\n');
    setContextMenu(null);
    // 同一グリッド内貼り付けで色を復元するため、コピーしたテキストを控える。
    lastCopiedTextRef.current = text;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      showError('クリップボードへのコピーに失敗しました。Ctrl+Cをお試しください。');
    }
  };

  // Copy ALL data (all rows) with column headers as a TSV string to the
  // clipboard. Row numbers are excluded. This mimics Excel's "select all +
  // copy" behavior. Called from the context menu "すべてコピー" item and from
  // Ctrl+C when the selectAll cell was clicked (all cells highlighted).
  const handleCopyAll = async () => {
    const inst = spreadsheetRef.current;
    if (!inst) {
      setContextMenu(null);
      return;
    }
    const currentOrder = columnOrderRef.current;
    // "すべてコピー" deliberately includes HIDDEN columns too (per spec),
    // so we do not filter by hiddenColumns here — all columns in the current
    // display order are exported.
    const headers = currentOrder.map((origIdx) => COLUMNS[origIdx].title);
    const data = inst.getData() as (string | number | null)[][];
    const lines: string[] = [headers.join('\t')];
    for (let r = 0; r < data.length; r++) {
      const row = data[r];
      lines.push(row.map((v) => String(v ?? '')).join('\t'));
    }
    const text = lines.join('\n');
    setContextMenu(null);
    try {
      await navigator.clipboard.writeText(text);
      showSuccess('全データをコピーしました');
    } catch {
      showError('クリップボードへのコピーに失敗しました。');
    }
  };

  // Paste from the clipboard into the top-left cell of the originally
  // right-clicked range (same as Ctrl+V, but works from the context menu too).
  const handleContextPaste = async () => {
    const range = contextMenu?.range;
    setContextMenu(null);
    if (!range) return;
    try {
      const text = await navigator.clipboard.readText();
      if (!text) return;
      const [x, y] = range;
      const inst = spreadsheetRef.current;
      if (!inst) return;
      // クリップボードの内容が直前の「右クリック→コピー」と一致する場合は、
      // jspreadsheet の内部コピーデータ（inst.data）を paste に渡す。paste は
      // テキストのハッシュがコピー時のハッシュと一致したときだけセル色を復元
      // するため、独自生成TSVを渡すと色が復元されない。内部データなら必ず一致する。
      const useInternal = text === lastCopiedTextRef.current && typeof inst.data === 'string';
      inst.paste(x, y, useInternal ? inst.data : text);
    } catch {
      showError('クリップボードから貼り付けできませんでした。Ctrl+Vをお試しください。');
    }
  };

  // Apply a background color to every cell in the current selection (or the
  // single right-clicked cell). Uses jspreadsheet-ce's own setStyle, so the
  // change is automatically tracked in Undo/Redo history.
  const handleSetCellColor = (color: string | null) => {
    const inst = spreadsheetRef.current;
    if (!inst || !contextMenu) return;
    // Build the target cell list directly from the snapshotted range,
    // rather than relying on `inst.highlighted` (which was reset when the
    // portal-rendered context menu opened).
    const [x1, y1, x2, y2] = contextMenu.range;
    const colStart = Math.min(x1, x2);
    const colEnd = Math.max(x1, x2);
    const rowStart = Math.min(y1, y2);
    const rowEnd = Math.max(y1, y2);
    const targets: HTMLTableCellElement[] = [];
    for (let r = rowStart; r <= rowEnd; r++) {
      for (let c = colStart; c <= colEnd; c++) {
        const cell = inst.getCellFromCoords(c, r);
        if (cell) targets.push(cell);
      }
    }
    if (targets.length === 0) {
      setContextMenu(null);
      return;
    }
    if (color) {
      inst.setStyle(targets, 'background-color', color);
    } else {
      // Clear color: set background-color to an empty-looking value via
      // jspreadsheet's own setStyle so the change is tracked in Undo history.
      // We cannot pass '' (empty string) because setStyle checks `if(t&&o)`
      // and skips when `o` is falsy. Instead use a valid CSS value that
      // renders as "no color". After setStyle records the history entry, we
      // then remove the property from inline style so no residual value
      // remains, and patch the internal style record to remove background-color.
      inst.setStyle(targets, 'background-color', 'transparent');
      // Now clean up the actual DOM + internal record so the cell truly has
      // no background-color (transparent can interfere with sticky column bg).
      targets.forEach((cell) => {
        cell.style.removeProperty('background-color');
        const colIdx = Number(cell.getAttribute('data-x'));
        const rowIdx = Number(cell.getAttribute('data-y'));
        const cellName = cellNameFromCoords(colIdx, rowIdx);
        const styleMap = (inst as any).options?.style ?? (inst as any).style;
        if (styleMap && styleMap[cellName]) {
          const cleaned = String(styleMap[cellName])
            .split(';')
            .map((s: string) => s.trim())
            .filter((s: string) => s && !/^background-color\s*:/i.test(s))
            .join('; ');
          if (cleaned) {
            styleMap[cellName] = cleaned;
          } else {
            delete styleMap[cellName];
          }
        }
      });
    }
    // Mark every affected row's META dirty so the color change is persisted
    // (via PATCH /cell-meta) on save, independently of content changes.
    for (let r = rowStart; r <= rowEnd; r++) {
      markRowMetaDirtyByIndex(r);
    }
    // Re-apply the visual selection highlight after the style change.
    inst.updateSelectionFromCoords(x1, y1, x2, y2);
    setContextMenu(null);
    updateUndoRedoButtons();
    if (colorFilterSelectionsRef.current.size > 0) {
      applyColumnFilters();
    }
  };

  // Open the comment editor dialog for the right-clicked cell.
  const handleOpenCommentDialog = () => {
    if (!contextMenu) return;
    const inst = spreadsheetRef.current;
    const cellName = cellNameFromCoords(contextMenu.col, contextMenu.row);
    const existing = (inst?.getComments(cellName) as string) || '';
    setCommentDialog({ col: contextMenu.col, row: contextMenu.row, value: existing });
    setContextMenu(null);
  };

  const handleSaveComment = () => {
    if (!commentDialog) return;
    const cellName = cellNameFromCoords(commentDialog.col, commentDialog.row);
    spreadsheetRef.current?.setComments(cellName, commentDialog.value.trim(), '');
    markRowMetaDirtyByIndex(commentDialog.row);
    setCommentDialog(null);
  };

  const handleClearComment = () => {
    if (!commentDialog) return;
    const cellName = cellNameFromCoords(commentDialog.col, commentDialog.row);
    spreadsheetRef.current?.setComments(cellName, '', '');
    markRowMetaDirtyByIndex(commentDialog.row);
    setCommentDialog(null);
  };

  // Undo / Redo (jspreadsheet-ce built-in history).
  // NOTE: undo/redo of row insert/delete bypasses our insertRow/ondeleterow
  // Undo / Redo button state: enabled only when history exists in that direction.
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // Shared function to check jspreadsheet's internal history and update
  // the undo/redo button states. Called after any action that modifies
  // history (cell edit, undo, redo, insert/delete row, sort, color change).
  const updateUndoRedoButtons = useCallback(() => {
    const inst = spreadsheetRef.current as any;
    if (!inst) {
      setCanUndo(false);
      setCanRedo(false);
      return;
    }
    const history: unknown[] = inst.history ?? [];
    const historyIndex: number = inst.historyIndex ?? -1;
    setCanUndo(historyIndex >= 0);
    setCanRedo(historyIndex < history.length - 1);
    // Also refresh dirty counts (these change at the same times)
    updateDirtyCounts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // hooks, so contractIds must be resynced manually via onundo/onredo below
  // to avoid misaligning contractIds[i] with the wrong grid row.
  const handleUndo = () => {
    spreadsheetRef.current?.undo();
    updateUndoRedoButtons();
  };
  const handleRedo = () => {
    spreadsheetRef.current?.redo();
    updateUndoRedoButtons();
  };

  // Toggle the ▼ filter icon's visibility/clickability on column headers.
  const handleToggleFilter = () => {
    setShowFilters((prev) => {
      const next = !prev;
      // When turning filters OFF, clear any active free-text search so rows
      // hidden by it become visible again (the search bar itself is hidden).
      if (!next && searchText) {
        setSearchText('');
        spreadsheetRef.current?.resetSearch();
      }
      return next;
    });
  };

  // Custom search using JSS CE search API
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
    if (spreadsheetRef.current) {
      if (value.trim()) {
        spreadsheetRef.current.search(value);
      } else {
        spreadsheetRef.current.resetSearch();
      }
    }
  };

  // Template download
  const handleTemplateDownload = async () => {
    try {
      const res = await api.get('/contracts/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'contract_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      showError('テンプレートのダウンロードに失敗しました');
    }
  };

  // CSV import
  const handleImportChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/contracts/import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { successCount, errorCount } = res.data;
      showSuccess(`インポート完了: 成功${successCount}件, 失敗${errorCount}件`);
      queryClient.invalidateQueries({ queryKey: ['contracts-spreadsheet'] });
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    } catch {
      showError('CSVインポートに失敗しました');
    }
    event.target.value = '';
  };

  // ============================================================
  // Unsaved changes guard (beforeunload + React Router blocker)
  // ============================================================
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // React Router navigation guard: since this app uses BrowserRouter (not
  // createBrowserRouter/RouterProvider), the useBlocker hook is unavailable.
  // Instead, we use a simpler approach: intercept link clicks via a
  // beforeunload event (already handled above for tab close/refresh) and
  // show the confirm dialog when the user clicks navigation items that would
  // leave this page. The Layout navigation is handled via navigate(), which
  // doesn't trigger beforeunload, so we override it with window.onpopstate.
  // For internal navigation via sidebar links (which use navigate()), we
  // rely on the beforeunload handler for browser-level exits and add a
  // custom prompt for in-app navigation via onbeforeunload-like behavior.
  const [navDialogOpen, setNavDialogOpen] = useState(false);
  const pendingNavigationRef = useRef<string | null>(null);
  const navResolverRef = useRef<((allowed: boolean) => void) | null>(null);

  // Register a navigation guard that shows the unsaved-changes dialog.
  const { registerGuard } = useNavigationGuard();
  useEffect(() => {
    const unregister = registerGuard((targetPath) => {
      // Check if there are unsaved changes (read from refs, not state, to
      // avoid stale closures).
      const hasDirty =
        dirtyContentRowIdsRef.current.size > 0 ||
        dirtyMetaRowIdsRef.current.size > 0 ||
        pendingDeleteIdsRef.current.size > 0 ||
        contractIdsRef.current.some((id) => id.startsWith('__new_'));
      if (!hasDirty) return true; // no changes → allow navigation
      // Show the confirmation dialog and return a Promise that resolves when
      // the user picks an option.
      pendingNavigationRef.current = targetPath;
      setNavDialogOpen(true);
      return new Promise<boolean>((resolve) => {
        navResolverRef.current = resolve;
      });
    });
    return unregister;
  }, [registerGuard]);

  const handleNavSaveAndLeave = async () => {
    setNavDialogOpen(false);
    await handleSaveAll();
    navResolverRef.current?.(true);
    navResolverRef.current = null;
  };
  const handleNavDiscardAndLeave = () => {
    setNavDialogOpen(false);
    dirtyContentRowIdsRef.current.clear();
    dirtyMetaRowIdsRef.current.clear();
    pendingDeleteIdsRef.current.clear();
    setDirtyCounts({ modified: 0, added: 0, deleted: 0 });
    navResolverRef.current?.(true);
    navResolverRef.current = null;
  };
  const handleNavCancel = () => {
    setNavDialogOpen(false);
    navResolverRef.current?.(false);
    navResolverRef.current = null;
  };

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ m: 2 }}>契約データの取得に失敗しました。</Alert>;

  return (
    <>
    <GlobalStyles styles={{
      // jsuites renders the calendar popup as a direct child of <body> (or
      // the jexcel_content container), outside of our React component tree.
      // These global styles ensure proper sizing and layout.
      // The actual visible popup box is .jcalendar-content (position:absolute
      // with no width in the library CSS), so it collapses to the width of its
      // positioning context (the narrow spreadsheet cell). Give it an explicit
      // width; .jcalendar itself already has min-width:280px in jsuites.css.
      '.jcalendar-content': {
        width: '300px !important',
      },
      '.jcalendar-table > table': {
        tableLayout: 'fixed !important' as const,
        width: '100% !important',
      },
      '.jcalendar-table td': {
        textAlign: 'center !important' as const,
        padding: '4px !important',
      },
      '.jcalendar-header': {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
      '.jcalendar-year': {
        order: 1,
      },
      '.jcalendar-year::after': {
        content: '"年"',
      },
      '.jcalendar-month': {
        order: 2,
      },
    }} />
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      // Height budget: full viewport minus the fixed AppBar (64px) minus the
      // main content's top padding (24px from Layout's `p: 3`). We only
      // subtract the TOP padding (not the bottom) and cancel the bottom
      // padding with a negative margin below, so the spreadsheet extends
      // nearly to the bottom edge of the viewport.
      height: 'calc(100vh - 48px - 24px)',
      // Reclaim the main content's bottom padding (24px) so the grid can grow
      // further down instead of leaving an empty gap under it.
      mb: -3,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <Box sx={{ flexShrink: 0, pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>契約管理{isDirty ? ' *' : ''}</Typography>
            {urlCustomerName && (
              <Chip label={urlCustomerName} color="primary" variant="outlined" size="small" />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isAdmin && (
              <>
                <Tooltip title="元に戻す (Ctrl+Z)">
                  <span>{/* span wrapper needed for tooltip on disabled button */}
                  <IconButton size="small" onClick={handleUndo} disabled={!canUndo} color={canUndo ? 'primary' : 'default'}>
                    <UndoIcon fontSize="small" />
                  </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="やり直し (Ctrl+Y)">
                  <span>
                  <IconButton size="small" onClick={handleRedo} disabled={!canRedo} color={canRedo ? 'primary' : 'default'}>
                    <RedoIcon fontSize="small" />
                  </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
            <Button
              size="small"
              variant={showFilters ? 'contained' : 'outlined'}
              startIcon={<FilterListIcon />}
              onClick={handleToggleFilter}
              color={showFilters ? 'primary' : 'inherit'}
            >
              フィルター
            </Button>
            <Button
              size="small"
              variant={hiddenColumns.size > 0 ? 'contained' : 'outlined'}
              startIcon={<ViewColumnIcon />}
              onClick={() => setColumnVisibilityDialogOpen(true)}
              color={hiddenColumns.size > 0 ? 'primary' : 'inherit'}
            >
              表示列{hiddenColumns.size > 0 ? `（${COLUMNS.length - hiddenColumns.size}/${COLUMNS.length}）` : ''}
            </Button>
            {isAdmin && (
              <>
                <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleTemplateDownload}>
                  テンプレート
                </Button>
                <Button size="small" variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                  CSVインポート
                  <input type="file" accept=".csv" hidden onChange={handleImportChange} />
                </Button>
                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleAddRow}>
                  行追加
                </Button>
                <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSaveAll}>
                  一括保存
                </Button>
              </>
            )}
          </Box>
        </Box>
        {/* Search bar - only visible when filters are active */}
        {showFilters && (
          <TextField
            value={searchText}
            onChange={handleSearchChange}
            placeholder="顧客、システム名、担当者、契約形態で検索..."
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        )}
      </Box>

      {/* Spreadsheet */}
      <Box
        onContextMenu={(e) => {
          e.preventDefault();
          // Try to determine which cell was right-clicked
          const target = e.target as HTMLElement;
          const cell = target.closest('td');
          let col = 0;
          let row = 0;
          if (cell && cell.dataset) {
            col = parseInt(cell.dataset.x || '0', 10);
            row = parseInt(cell.dataset.y || '0', 10);
          }
          // Detect whether the right-click landed on a column header cell
          // (thead) — the column hide/unhide menu items only apply there.
          const isHeader = !!target.closest('thead');
          // If the right-clicked cell is part of the current highlighted
          // (multi-cell) selection, keep that selection's full range;
          // otherwise treat this as a single-cell selection.
          const inst = spreadsheetRef.current;
          const highlighted = inst?.highlighted as HTMLTableCellElement[] | undefined;
          const isPartOfSelection = !!highlighted?.some((el) => el === cell);
          const hasRangeSelection = isPartOfSelection && (highlighted?.length ?? 0) > 1;
          let range: [number, number, number, number] = [col, row, col, row];
          if (isPartOfSelection && inst?.selectedCell) {
            const [x1, y1, x2, y2] = inst.selectedCell as [number, number, number, number];
            range = [Number(x1), Number(y1), Number(x2), Number(y2)];
          } else {
            // Apply single-cell selection immediately so the visual
            // highlight matches what the context menu will act on.
            inst?.updateSelectionFromCoords(col, row, col, row);
          }
          setContextMenu({ mouseX: e.clientX, mouseY: e.clientY, col, row, hasRangeSelection, range, isHeader });
        }}
        sx={{
          flexGrow: 1,
          minHeight: 0,
          // The outer box must NOT scroll. `tableOverflow: true` makes
          // jspreadsheet's own `.jexcel_content` the single scroll container
          // (both axes). If this box also had `overflow: auto`, the content
          // would scroll here AND inside `.jexcel_content`, producing two
          // overlapping vertical scrollbars. Keeping this `hidden` leaves the
          // spreadsheet's internal element as the only scroller.
          overflow: 'hidden',
          // The spreadsheet table is wider/taller than this container (no
          // tableWidth:'100%' is set on jspreadsheet), so scrolling happens
          // entirely inside `.jexcel_content`. The header/sidebar stay fixed.
          '& .jexcel_content': {
            overflow: 'auto !important',
            width: '100% !important',
          },
          '& .jexcel': {
            height: '100%',
          },
          // Excel-like hidden-column indicator: the visible header cell that
          // immediately follows a hidden run gets a thicker left border so the
          // user can tell columns are collapsed there.
          '& .jexcel thead td.hidden-col-boundary': {
            borderLeft: '3px double #666 !important',
          },
          // Column resize handle area: show col-resize cursor on the rightmost
          // 6px of each header cell (where jspreadsheet's drag-resize starts).
          '& .jexcel thead td[data-x]': {
            cursor: 'default',
          },
          // Excel-like ▼ filter icon on every header cell — only shown while
          // the "フィルター" toolbar button is active (showFilters). Clicking
          // within its hit area (rightmost 22px of the cell, handled in
          // onClickCapture below, also gated on showFilters) opens the custom
          // filter dialog instead of jspreadsheet-ce's built-in filter row.
          ...(showFilters && {
            '& .jexcel thead td[data-x]': {
              position: 'relative',
              paddingRight: '20px !important',
            },
            '& .jexcel thead td[data-x]::after': {
              content: '"▼"',
              position: 'absolute',
              right: '6px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '9px',
              color: '#888',
              cursor: 'pointer',
            },
            // Filled/blue ▼ indicates this column currently has an active filter.
            '& .jexcel thead td[data-x].column-filter-active::after': {
              color: '#1976d2',
            },
          }),
          // General dropdown styling (border, shadow, scroll).
          // NOTE: the border/shadow/rounding must live on .jdropdown-content
          // (the actual option list), NOT on .jdropdown-container (its
          // parent, sized/positioned by jsuites' own JS). Putting
          // `overflow: hidden` on .jdropdown-container — as a previous
          // version of this file did, to get rounded corners — clipped the
          // container to its own (sometimes zero-height) box and made the
          // entire option list invisible for dropdown-type cells (顧客 column).
          // A cell being edited (jsuites adds the `.editor` class) that is
          // ALSO one of our frozen sticky columns creates its own stacking
          // context via `position: sticky; z-index: N`. That traps the
          // dropdown's option list inside the cell's stacking context, where
          // it gets painted underneath the neighboring frozen columns and
          // becomes invisible (the 顧客 column symptom). Bumping the editing
          // cell's own z-index far above the other frozen columns lets its
          // child dropdown paint on top of them.
          //
          // IMPORTANT: the selector below intentionally includes `[data-x]`
          // so its specificity (0,3,2) beats the frozen-column rules
          // `td[data-x="0"]` / `td[data-x="1"]` (0,2,2) further down. Both
          // sets use !important, so with equal specificity the *later* rule
          // would otherwise win and clamp z-index back to 11 — which is
          // exactly why earlier attempts at this fix had no effect.
          '& .jexcel tbody td.editor[data-x]': {
            zIndex: '20001 !important',
            overflow: 'visible !important',
          },
          // The cell-editor dropdown (顧客 column) is positioned by jsuites
          // as `position: fixed` (because tableOverflow is on). Our frozen
          // columns set z-index up to 13, and a fixed element with no
          // explicit z-index would render *below* them — hiding the option
          // list. Force a very high z-index so the dropdown always paints on
          // top of the frozen columns/headers.
          '& .jdropdown-container': {
            backgroundColor: '#fff !important',
            zIndex: '20000 !important',
          },
          '& .jdropdown-content': {
            backgroundColor: '#fff !important',
            maxHeight: '400px !important',
            overflowY: 'auto !important',
            border: '1px solid #ccc !important',
            borderRadius: '4px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15) !important',
            zIndex: '20000 !important',
          },
          '& .jdropdown-close': {
            display: 'block !important',
            backgroundColor: '#1976d2 !important',
            color: '#fff !important',
            textAlign: 'center !important',
            padding: '6px 12px !important',
            cursor: 'pointer',
            fontWeight: 'bold',
          },
          '& .jdropdown-item': {
            padding: '6px 12px !important',
            borderBottom: '1px solid #f0f0f0 !important',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#e3f2fd !important',
            },
          },
          '& .jdropdown-item.jdropdown-selected': {
            backgroundColor: '#bbdefb !important',
            color: '#000 !important',
          },
          // Hover/selected text stays black instead of the library's default white
          // (which is hard to read against the light blue highlight).
          '& .jdropdown-default .jdropdown-item:hover': {
            color: '#000 !important',
          },
          // Hide built-in search (using custom MUI search instead)
          '& .jexcel_search': {
            display: 'none !important',
          },
          // Calendar styles moved to GlobalStyles (rendered outside this Box)
          // Freeze the leftmost 3 "columns" (row-number index + the first 2
          // display columns) during horizontal scroll, using plain CSS
          // sticky positioning instead of jspreadsheet-ce's own
          // freezeColumns option. That built-in feature hardcodes a "-51px"
          // offset assuming a fixed-width row-number column, which conflicts
          // with our custom sticky row-number column and caused the 顧客
          // column to render underneath/behind it. Left offsets for the two
          // data columns are computed from their real widths in
          // `updateFreezeOffsets` (see spreadsheet init effect) and applied
          // via the --freeze-offset-col0 / --freeze-offset-col1 CSS vars.
          // Header (thead) frozen cells need BOTH top:0 (vertical sticky,
          // matching all other header cells) and left (horizontal sticky)
          // set explicitly on the same rule — otherwise some browsers only
          // honor one axis when the two offsets come from rules of differing
          // origin/specificity. (The old built-in filter row, which also
          // lived inside <thead>, no longer exists — filters:false — so no
          // extra scoping is needed here.)
          //
          // General rule: every header cell (支払月, 月額, 5年計, etc. —
          // i.e. everything beyond the two explicitly-frozen columns below)
          // must stay vertically sticky on scroll. jspreadsheet.css's own
          // base rule (`.jexcel > thead > tr > td { position: sticky; top:
          // 0 }`) normally provides this, but the showFilters ▼-icon rule
          // above sets `position: relative` (needed so the ::after icon
          // positions relative to the cell) WITHOUT !important, which was
          // silently overriding the library's sticky positioning and let
          // those headers scroll away vertically. Re-assert sticky/top here
          // with !important so it wins regardless of rule order — `position:
          // sticky` is still a valid positioning context for the ::after
          // icon, so this doesn't affect its placement.
          '& .jexcel thead > tr:not(.jexcel_filter) > td': {
            position: 'sticky !important',
            top: '0 !important',
            textAlign: 'center !important',
            // Use box-shadow for the bottom edge instead of border. A real
            // border on a sticky element can shift by 1px on scroll due to
            // subpixel rendering; box-shadow is painted on top and never moves.
            borderBottom: 'none !important',
            boxShadow: '0 1px 0 0 #ccc',
          },
          '& .jexcel thead td.jexcel_selectall': {
            position: 'sticky !important',
            top: '0 !important',
            left: '0 !important',
            zIndex: '13 !important',
            backgroundColor: '#f3f3f3 !important',
          },
          '& .jexcel thead td[data-x="0"]': {
            position: 'sticky !important',
            top: '0 !important',
            left: 'var(--freeze-offset-col0, 0px) !important',
            zIndex: '12 !important',
            backgroundColor: '#f3f3f3 !important',
          },
          '& .jexcel thead td[data-x="1"]': {
            position: 'sticky !important',
            top: '0 !important',
            left: 'var(--freeze-offset-col1, 0px) !important',
            zIndex: '11 !important',
            backgroundColor: '#f3f3f3 !important',
            boxShadow: '2px 0 2px 0 rgba(0,0,0,0.15) !important',
          },
          // Body (tbody) frozen cells: horizontal sticky only. The
          // background is intentionally set WITHOUT !important so that
          // jspreadsheet-ce's own setStyle() (which writes an inline
          // background-color on the cell) can still win — a stylesheet rule
          // with !important always beats inline styles regardless of
          // specificity, which was silently overriding user-applied cell
          // colors on these two columns.
          // Frozen (sticky) columns MUST keep an OPAQUE background at all
          // times. The root cause of the "ghosting" artifact was NOT a repaint
          // bug: when a row is selected, the `.highlight` rule applies a
          // SEMI-TRANSPARENT green (rgba(...,0.10)) background to these cells,
          // and since that rule is defined later with equal specificity it
          // wins over the frozen column's own background. The sticky cell then
          // becomes translucent, letting the horizontally-scrolled column
          // underneath show through — which looks like doubled/overlapping
          // text. Cells the user explicitly colored don't show it because
          // their opaque inline color hides whatever is beneath.
          // Fix: force an opaque background with `!important` so the selection
          // tint can never make these frozen cells see-through.
          '& .jexcel tbody td.jexcel_row': {
            position: 'sticky !important',
            left: '0 !important',
            zIndex: '12 !important',
            backgroundColor: '#f3f3f3 !important',
            borderRight: 'none !important',
            boxShadow: '1px 0 0 0 #ccc',
          },
          // For the two frozen DATA columns we still want user-applied cell
          // colors (inline background-color set via setStyle) to win, so we
          // do NOT use !important here. Instead we neutralize only the
          // selection-highlight translucency by overriding the .highlight
          // background specifically for these sticky columns to an opaque
          // white further below.
          '& .jexcel tbody td[data-x="0"]': {
            position: 'sticky !important',
            left: 'var(--freeze-offset-col0, 0px) !important',
            zIndex: '11 !important',
            backgroundColor: '#fff',
          },
          '& .jexcel tbody td[data-x="1"]': {
            position: 'sticky !important',
            left: 'var(--freeze-offset-col1, 0px) !important',
            zIndex: '10 !important',
            backgroundColor: '#fff',
            boxShadow: '2px 0 2px 0 rgba(0,0,0,0.15)',
          },
          // When a frozen data column cell is part of the selection, keep its
          // background OPAQUE white instead of the translucent green tint (see
          // the ghosting explanation above). This selector is more specific
          // (`td.highlight[data-x="0"]`) than the plain `.highlight` rule, so
          // it wins without needing !important — preserving the ability for
          // user-applied inline colors (even higher priority) to still show.
          // The color used here is the OPAQUE equivalent of the selection tint
          // `rgba(33, 163, 102, 0.10)` composited over white (#fff), so the
          // frozen columns look identical to the other selected cells while
          // staying fully opaque (an actual translucent color would let the
          // horizontally-scrolled columns underneath show through).
          '& .jexcel tbody td.highlight[data-x="0"], & .jexcel tbody td.highlight[data-x="1"]': {
            backgroundColor: '#e9f6f0',
          },
          '& .jexcel tbody td.highlight.jexcel_row': {
            backgroundColor: '#f3f3f3',
          },
          // ==========================================================
          // Excel-like selection styling (green border ~#21A366)
          // ==========================================================
          // jspreadsheet-ce marks the outer edges of the selected range with
          // the classes .highlight-left/-right/-top/-bottom on the boundary
          // cells, and highlighted cells carry .highlight. We override the
          // border color/width to a 2px Excel green, and tint the range fill
          // a faint green. These are pure CSS overrides (kept during drag too,
          // since the classes persist throughout selection) — no JS changes.
          '& .jexcel tbody td.highlight': {
            // Use a non-!important background so that user-applied cell colors
            // (set via inline style by jspreadsheet's setStyle) always win.
            // The selection tint only shows on cells that have no inline color.
            backgroundColor: 'rgba(33, 163, 102, 0.10)',
          },
          // Draw the 2px green range border with INSET box-shadow instead of
          // `border`. A real border adds to the cell's box size, which shifted
          // column/row widths whenever a cell became selected. box-shadow is
          // painted without affecting layout, so cell dimensions stay fixed.
          //
          // IMPORTANT: box-shadow is a SINGLE property, so separate CSS rules
          // do NOT stack — the last matching rule wins entirely. A cell on the
          // boundary can carry several highlight-* classes at once (e.g. a
          // middle cell of a horizontal 1-row selection has BOTH highlight-top
          // AND highlight-bottom; a middle cell of a vertical 1-col selection
          // has BOTH highlight-left AND highlight-right). To keep every needed
          // edge, we enumerate ALL 15 edge combinations and give each a single
          // combined box-shadow. Edge shadow parts:
          //   L = inset  2px 0 0 0, R = inset -2px 0 0 0,
          //   T = inset 0  2px 0 0, B = inset 0 -2px 0 0   (all #21A366)
          // 1 edge
          '& .jexcel tbody td.highlight-left': { boxShadow: 'inset 2px 0 0 0 #21A366' },
          '& .jexcel tbody td.highlight-right': { boxShadow: 'inset -2px 0 0 0 #21A366' },
          '& .jexcel tbody td.highlight-top': { boxShadow: 'inset 0 2px 0 0 #21A366' },
          '& .jexcel tbody td.highlight-bottom': { boxShadow: 'inset 0 -2px 0 0 #21A366' },
          // 2 edges
          '& .jexcel tbody td.highlight-left.highlight-right': {
            boxShadow: 'inset 2px 0 0 0 #21A366, inset -2px 0 0 0 #21A366',
          },
          '& .jexcel tbody td.highlight-top.highlight-bottom': {
            boxShadow: 'inset 0 2px 0 0 #21A366, inset 0 -2px 0 0 #21A366',
          },
          '& .jexcel tbody td.highlight-left.highlight-top': {
            boxShadow: 'inset 2px 0 0 0 #21A366, inset 0 2px 0 0 #21A366',
          },
          '& .jexcel tbody td.highlight-right.highlight-top': {
            boxShadow: 'inset -2px 0 0 0 #21A366, inset 0 2px 0 0 #21A366',
          },
          '& .jexcel tbody td.highlight-left.highlight-bottom': {
            boxShadow: 'inset 2px 0 0 0 #21A366, inset 0 -2px 0 0 #21A366',
          },
          '& .jexcel tbody td.highlight-right.highlight-bottom': {
            boxShadow: 'inset -2px 0 0 0 #21A366, inset 0 -2px 0 0 #21A366',
          },
          // 3 edges
          '& .jexcel tbody td.highlight-left.highlight-right.highlight-top': {
            boxShadow: 'inset 2px 0 0 0 #21A366, inset -2px 0 0 0 #21A366, inset 0 2px 0 0 #21A366',
          },
          '& .jexcel tbody td.highlight-left.highlight-right.highlight-bottom': {
            boxShadow: 'inset 2px 0 0 0 #21A366, inset -2px 0 0 0 #21A366, inset 0 -2px 0 0 #21A366',
          },
          '& .jexcel tbody td.highlight-left.highlight-top.highlight-bottom': {
            boxShadow: 'inset 2px 0 0 0 #21A366, inset 0 2px 0 0 #21A366, inset 0 -2px 0 0 #21A366',
          },
          '& .jexcel tbody td.highlight-right.highlight-top.highlight-bottom': {
            boxShadow: 'inset -2px 0 0 0 #21A366, inset 0 2px 0 0 #21A366, inset 0 -2px 0 0 #21A366',
          },
          // 4 edges (single selected cell)
          '& .jexcel tbody td.highlight-left.highlight-right.highlight-top.highlight-bottom': {
            boxShadow: 'inset 2px 0 0 0 #21A366, inset -2px 0 0 0 #21A366, inset 0 2px 0 0 #21A366, inset 0 -2px 0 0 #21A366',
          },
          // The active cell (anchor of the selection) — give it a slightly
          // stronger look, similar to Excel's active-cell outline, and keep
          // its own background white so the anchor stands out from the fill.
          '& .jexcel tbody td.highlight-selected': {
            backgroundColor: 'rgba(33, 163, 102, 0.04)',
          },
        }}
        onClickCapture={(e) => {
          const target = e.target as HTMLElement;
          const container = jRef.current;
          if (!container) return;

          // Relabel any "Done" close buttons in the whole spreadsheet (the
          // customer cell editor dropdown uses jsuites' hardcoded English
          // "Done" text).
          const relabelClose = () => {
            container.querySelectorAll('.jdropdown-close').forEach((btn) => {
              if (btn.textContent === 'Done') btn.textContent = '閉じる';
            });
          };
          [0, 10, 30, 60, 120].forEach((delay) => setTimeout(relabelClose, delay));

          // ▼ filter icon click: header cells are <td data-x="N">Title</td>,
          // and the ▼ icon is rendered via ::after (see sx block, only when
          // showFilters is true), positioned over the right edge of the
          // cell. We treat any click within the rightmost 22px of a header
          // cell as an icon click, opening the filter dialog instead of
          // letting jspreadsheet-ce's default header-click (column sort)
          // behavior run. Gated on showFilters so the icon is only
          // clickable while the "フィルター" toolbar button is active.
          const headerCell = showFilters ? (target.closest('thead td[data-x]') as HTMLElement | null) : null;
          if (headerCell) {
            const rect = headerCell.getBoundingClientRect();
            const clickX = (e as unknown as React.MouseEvent).clientX;
            if (rect.right - clickX <= 22) {
              e.stopPropagation();
              e.preventDefault();
              const displayCol = Number(headerCell.getAttribute('data-x'));
              openFilterDialog(displayCol);
            }
          }
        }}
      >
        <div ref={jRef} style={{ width: '100%', height: '100%' }} />
      </Box>

      {/* Status bar (Excel-like selection stats) */}
      <Box
        sx={{
          flexShrink: 0,
          height: 26,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 2,
          px: 1.5,
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f5f5f5',
          fontSize: '12px',
          color: '#555',
          userSelect: 'none',
        }}
      >
        {isDirty && (
          <Box sx={{ display: 'flex', gap: 1.5, color: '#d32f2f', fontWeight: 500 }}>
            {dirtyCounts.modified > 0 && <span>修正: {dirtyCounts.modified}件</span>}
            {dirtyCounts.added > 0 && <span>追加: {dirtyCounts.added}件</span>}
            {dirtyCounts.deleted > 0 && <span>削除: {dirtyCounts.deleted}件</span>}
          </Box>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {statusBar && (
          <>
            <span>選択セル数: {statusBar.count}</span>
            {statusBar.numericCount > 0 && (
              <>
                <span>数値セル数: {statusBar.numericCount}</span>
                <span>合計: {statusBar.sum.toLocaleString()}</span>
                <span>平均: {statusBar.avg.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                <span>最小: {statusBar.min.toLocaleString()}</span>
                <span>最大: {statusBar.max.toLocaleString()}</span>
              </>
            )}
          </>
        )}
      </Box>

      {/* Custom context menu (MUI Popup) */}
      <Menu
        open={contextMenu !== null}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
      >
        <MuiMenuItem onClick={handleContextCopy}>
          <ListItemIcon><CopyIcon fontSize="small" /></ListItemIcon>
          <ListItemText>コピー</ListItemText>
        </MuiMenuItem>
        <MuiMenuItem onClick={handleCopyAll}>
          <ListItemIcon><CopyIcon fontSize="small" /></ListItemIcon>
          <ListItemText>すべてコピー（ヘッダー付き）</ListItemText>
        </MuiMenuItem>
        {isAdmin && (
          <MuiMenuItem onClick={handleContextPaste}>
            <ListItemIcon><PasteIcon fontSize="small" /></ListItemIcon>
            <ListItemText>貼り付け</ListItemText>
          </MuiMenuItem>
        )}
        <Divider />
        {/* Display positions 0 (顧客) and 1 (システム名) are the frozen
            columns and must stay put, so moving is only offered for
            columns at display position 3+ (can move left down to position 2,
            never into the frozen 0/1 zone), and the frozen columns themselves
            (0/1) offer no move option. */}
        {isAdmin && contextMenu && contextMenu.col > 2 && (
          <MuiMenuItem onClick={() => { spreadsheetRef.current?.moveColumn(contextMenu.col, contextMenu.col - 1); saveColumnStateToStorage(); setContextMenu(null); }}>
            <ListItemIcon><ArrowLeftIcon fontSize="small" /></ListItemIcon>
            <ListItemText>列を左に移動</ListItemText>
          </MuiMenuItem>
        )}
        {isAdmin && contextMenu && contextMenu.col >= 2 && contextMenu.col < COLUMNS.length - 1 && (
          <MuiMenuItem onClick={() => { spreadsheetRef.current?.moveColumn(contextMenu.col, contextMenu.col + 1); saveColumnStateToStorage(); setContextMenu(null); }}>
            <ListItemIcon><ArrowRightIcon fontSize="small" /></ListItemIcon>
            <ListItemText>列を右に移動</ListItemText>
          </MuiMenuItem>
        )}

        {/* ---- Column hide / unhide (Excel-like) ---- */}
        <Divider />
        {contextMenu && (
          <MuiMenuItem
            onClick={() => {
              const currentOrder = columnOrderRef.current;
              // Hide every column covered by the current selection range
              // (supports multi-column selection), skipping the frozen ones.
              const [x1, , x2] = contextMenu.range;
              const from = Math.min(x1, x2);
              const to = Math.max(x1, x2);
              const targets: number[] = [];
              for (let d = from; d <= to; d++) {
                const orig = currentOrder[d];
                if (orig === undefined) continue;
                // Never hide the two frozen key columns (display pos 0/1)
                if (d === 0 || d === 1) continue;
                targets.push(orig);
              }
              if (targets.length > 0) hideColumnsByOrigIdx(targets);
              setContextMenu(null);
            }}
            disabled={contextMenu.col <= 1}
          >
            <ListItemIcon><VisibilityOffIcon fontSize="small" /></ListItemIcon>
            <ListItemText>列を非表示</ListItemText>
          </MuiMenuItem>
        )}
        {contextMenu && getAdjacentHiddenOrigIdxs(contextMenu.col).length > 0 && (
          <MuiMenuItem
            onClick={() => {
              const toShow = getAdjacentHiddenOrigIdxs(contextMenu.col);
              if (toShow.length > 0) showColumnsByOrigIdx(toShow);
              setContextMenu(null);
            }}
          >
            <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
            <ListItemText>列を再表示</ListItemText>
          </MuiMenuItem>
        )}
        {hiddenColumns.size > 0 && (
          <MuiMenuItem
            onClick={() => {
              showAllColumns();
              setContextMenu(null);
            }}
          >
            <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
            <ListItemText>すべての列を再表示</ListItemText>
          </MuiMenuItem>
        )}

        {isAdmin && <Divider />}
        {isAdmin && (
          <MuiMenuItem onClick={() => {
            const row = contextMenu?.row ?? 0;
            // insertRow(numRows, rowIndex) without a 3rd arg inserts AFTER
            // the given row, i.e. the new row ends up at index `row + 1` in
            // the data array. contractIds must be spliced at the same index.
            spreadsheetRef.current?.insertRow(1, row);
            updateContractIds((prev) => {
              const next = [...prev];
              next.splice(row + 1, 0, `__new_${Date.now()}`);
              return next;
            });
            setContextMenu(null);
            applyColumnVisibility();
            applyRequiredFieldHighlight();
          }}>
            <ListItemIcon><InsertRowIcon fontSize="small" /></ListItemIcon>
            <ListItemText>行を追加（選択行の下）</ListItemText>
          </MuiMenuItem>
        )}
        {isAdmin && (
          <MuiMenuItem onClick={() => {
            // deleteRow() triggers the ondeleterow handler configured on the
            // spreadsheet, which removes the row from view and marks its id
            // for deletion (sent to the backend only on "一括保存"). Do not
            // duplicate that logic here.
            spreadsheetRef.current?.deleteRow(contextMenu?.row);
            setContextMenu(null);
          }}>
            <ListItemIcon><DeleteRowIcon fontSize="small" /></ListItemIcon>
            <ListItemText>行を削除</ListItemText>
          </MuiMenuItem>
        )}
        {/* MUI's Menu clones its children to inject keyboard-navigation props,
            which it cannot do through a Fragment ("The Menu component doesn't
            accept a Fragment as a child"). Conditional groups are therefore
            returned as an ARRAY of keyed elements rather than wrapped in <>. */}
        {isAdmin && [
          <Divider key="comment-divider" />,
          <MuiMenuItem key="comment" onClick={handleOpenCommentDialog}>
            <ListItemIcon><CommentIcon fontSize="small" /></ListItemIcon>
            <ListItemText>コメント編集</ListItemText>
          </MuiMenuItem>,
          <Divider key="color-divider" />,
          <Box key="color-label" sx={{ px: 2, py: 0.5, display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
            <PaletteIcon fontSize="small" />
            <Typography variant="caption">セル色</Typography>
          </Box>,
          // Wrap to a new row every 5 swatches. Each swatch is 22px wide plus a
          // 1px border on each side = 24px, with a 6px (gap: 0.75) gap between
          // them → 5 * 24 + 4 * 6 = 144px. Use a fixed 5-column CSS grid so it
          // always shows exactly 5 swatches per row regardless of menu width.
          <Box key="color-swatches" sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 22px)', gap: 0.75, px: 2, py: 0.5, justifyContent: 'start' }}>
            {CELL_COLOR_PALETTE.map((c) => (
              <Tooltip key={c.value} title={c.label}>
                <Box
                  onClick={() => handleSetCellColor(c.value)}
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    backgroundColor: c.value,
                    border: '1px solid #999',
                    cursor: 'pointer',
                    '&:hover': { transform: 'scale(1.15)' },
                  }}
                />
              </Tooltip>
            ))}
          </Box>,
          <MuiMenuItem key="color-clear" onClick={() => handleSetCellColor(null)}>
            <ListItemIcon><ClearColorIcon fontSize="small" /></ListItemIcon>
            <ListItemText>色をクリア</ListItemText>
          </MuiMenuItem>,
        ]}
      </Menu>

      {/* Navigation guard dialog (unsaved changes) */}
      <Dialog open={navDialogOpen} onClose={handleNavCancel} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <WarningIcon sx={{ color: '#ed6c02', fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>変更内容が保存されていません</Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>変更内容</Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {dirtyCounts.modified > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <EditIcon sx={{ fontSize: 18, color: '#1976d2' }} />
                  <Typography variant="body2">修正 {dirtyCounts.modified}件</Typography>
                </Box>
              )}
              {dirtyCounts.added > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AddCircleIcon sx={{ fontSize: 18, color: '#2e7d32' }} />
                  <Typography variant="body2">追加 {dirtyCounts.added}件</Typography>
                </Box>
              )}
              {dirtyCounts.deleted > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <DeleteIcon2 sx={{ fontSize: 18, color: '#d32f2f' }} />
                  <Typography variant="body2">削除 {dirtyCounts.deleted}件</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleNavCancel} variant="outlined">キャンセル</Button>
          <Button onClick={handleNavDiscardAndLeave} variant="outlined">保存せずに移動</Button>
          <Button onClick={handleNavSaveAndLeave} variant="contained" startIcon={<SaveIcon />}>保存して移動</Button>
        </DialogActions>
      </Dialog>

      {/* Column visibility dialog (表示列) */}
      <Dialog
        open={columnVisibilityDialogOpen}
        onClose={() => setColumnVisibilityDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>表示列の設定</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Button size="small" onClick={showAllColumns}>すべて表示</Button>
          </Box>
          <Divider sx={{ mb: 1 }} />
          <FormGroup>
            {columnOrderRef.current.map((origIdx) => {
              const isFrozen = origIdx === 0 || origIdx === 1;
              return (
                <FormControlLabel
                  key={origIdx}
                  control={
                    <Checkbox
                      size="small"
                      checked={!hiddenColumns.has(origIdx)}
                      onChange={() => toggleColumnVisibility(origIdx)}
                      disabled={isFrozen}
                    />
                  }
                  label={
                    <Typography variant="body2" component="span">
                      {COLUMNS[origIdx].title}
                      {isFrozen && (
                        <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 0.5 }}>
                          (固定)
                        </Typography>
                      )}
                    </Typography>
                  }
                />
              );
            })}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setColumnVisibilityDialogOpen(false)} variant="contained">閉じる</Button>
        </DialogActions>
      </Dialog>

      {/* Comment editor dialog */}
      <Dialog open={commentDialog !== null} onClose={() => setCommentDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>コメント編集</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            value={commentDialog?.value ?? ''}
            onChange={(e) => setCommentDialog((prev) => (prev ? { ...prev, value: e.target.value } : prev))}
            placeholder="コメントを入力..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClearComment} color="inherit">クリア</Button>
          <Button onClick={() => setCommentDialog(null)}>キャンセル</Button>
          <Button onClick={handleSaveComment} variant="contained">保存</Button>
        </DialogActions>
      </Dialog>

      {/* Excel-like column filter dialog */}
      <Dialog open={filterDialog !== null} onClose={handleFilterDialogCancel} maxWidth="xs" fullWidth>
        <DialogTitle>{filterDialog?.title} のフィルター</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField
            autoFocus
            size="small"
            fullWidth
            placeholder="候補を検索..."
            value={filterDialog?.search ?? ''}
            onChange={(e) => handleFilterDialogSearchChange(e.target.value)}
            autoComplete="new-password"
            inputProps={{ autoComplete: 'new-password', 'data-lpignore': 'true', 'data-form-type': 'other' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" onClick={handleFilterDialogSelectAll}>全選択</Button>
            <Button size="small" onClick={handleFilterDialogDeselectAll}>全解除</Button>
          </Box>
          <Divider />
          <Box sx={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #eee', borderRadius: 1 }}>
            <FormGroup sx={{ px: 1 }}>
              {filterDialog?.allValues
                .filter((v) => !filterDialog.search.trim() || v.toLowerCase().includes(filterDialog.search.trim().toLowerCase()))
                .map((value) => (
                  <FormControlLabel
                    key={value}
                    control={
                      <Checkbox
                        size="small"
                        checked={filterDialog.selected.has(value)}
                        onChange={() => handleFilterDialogToggleValue(value)}
                      />
                    }
                    label={value === '' ? '(空白)' : value}
                  />
                ))}
              {filterDialog && filterDialog.allValues.filter((v) => !filterDialog.search.trim() || v.toLowerCase().includes(filterDialog.search.trim().toLowerCase())).length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                  該当する候補がありません
                </Typography>
              )}
            </FormGroup>
          </Box>
          {/* Color filter section */}
          {filterDialog && filterDialog.allColors.length > 0 && (
            <>
              <Divider sx={{ mt: 1 }} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>色でフィルター</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                <Button size="small" onClick={() => setFilterDialog((prev) => prev ? { ...prev, selectedColors: new Set(prev.allColors) } : prev)}>全選択</Button>
                <Button size="small" onClick={() => setFilterDialog((prev) => prev ? { ...prev, selectedColors: new Set() } : prev)}>全解除</Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {filterDialog.allColors.map((color) => {
                  const isNone = color === '__none__';
                  const paletteEntry = CELL_COLOR_PALETTE.find((p) => p.value.toLowerCase() === color);
                  const label = isNone ? '色なし' : '';
                  const checked = filterDialog.selectedColors.has(color);
                  return (
                    <Box
                      key={color}
                      onClick={() => setFilterDialog((prev) => {
                        if (!prev) return prev;
                        const next = new Set(prev.selectedColors);
                        if (next.has(color)) next.delete(color); else next.add(color);
                        return { ...prev, selectedColors: next };
                      })}
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                        px: 1, py: 0.5, borderRadius: 1, cursor: 'pointer',
                        minHeight: 28, minWidth: 28,
                        border: checked ? '2px solid #1976d2' : '1px solid #ccc',
                        backgroundColor: isNone ? '#fff' : color,
                        opacity: checked ? 1 : 0.5,
                        '&:hover': { opacity: 1 },
                      }}
                    >
                      <Typography variant="caption" sx={{ color: isNone ? 'text.primary' : 'inherit', fontWeight: checked ? 'bold' : 'normal' }}>
                        {label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFilterDialogCancel}>キャンセル</Button>
          <Button onClick={handleFilterDialogOk} variant="contained">OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
    </>
  );
};

export default ContractSpreadsheetPage;
